<template>
  <div class="smart-ambiente">
    <div v-if="!barn" class="warning">
      <div class="text">
        <h1>Sem dados disponíveis</h1>
        <p>Não foi possível carregar o barracão.</p>
      </div>
    </div>
    <template v-else>
      <PageLoader v-if="loading" />

      <template v-else>
        <div v-if="!sensors.length" class="warning">
          <div class="text">
            <h1>Sem dados disponíveis</h1>
            <p>Não há sensores de temperatura e humidade neste barracão.</p>
          </div>
        </div>
        <section v-else class="sa-section">
          <header class="sa-page-head">
            <h2 class="sa-page-head__brand">SmartSense</h2>
            <p v-if="pageSensorSubtitle" class="sa-page-head__sensor">{{ pageSensorSubtitle }}</p>
          </header>
          <nav class="sa-subtabs" aria-label="SmartSense">
            <button
              type="button"
              class="sa-subtab"
              :class="{ 'sa-subtab--active': ambienteSubTab === 'resumo' }"
              :disabled="subTabSwitching"
              @click="selectSubTab('resumo')"
            >
              Resumo
            </button>
            <button
              type="button"
              class="sa-subtab"
              :class="{ 'sa-subtab--active': ambienteSubTab === 'sensores' }"
              :disabled="subTabSwitching"
              @click="selectSubTab('sensores')"
            >
              Sensores
            </button>
          </nav>

          <div v-if="ambienteSubTab === 'resumo'" class="sa-subtab-panel">
            <h3 class="sa-section__title">Última leitura</h3>
            <div v-if="subTabSwitching" class="sa-loading-inline">Carregando…</div>
            <div v-else-if="barnSnapshotStats.hasData" class="sa-snapshot">
              <p class="sa-snapshot__group-title">Temperatura (ºC)</p>
              <div class="sa-snapshot-row">
                <div class="sa-mini" :class="statCardToneClass('min', barnSnapshotStats.tempMin)">
                  <span class="sa-mini__label">Mínima</span>
                  <span class="sa-mini__value"
                    >{{ formatStatTemp(barnSnapshotStats.tempMin)
                    }}<span v-if="barnSnapshotStats.tempMin != null" class="sa-mini__unit">ºC</span></span
                  >
                  <span class="sa-mini__time">{{ formatSnapshotAt(barnSnapshotStats.tempMinAt) }}</span>
                </div>
                <div class="sa-mini" :class="statCardToneClass('max', barnSnapshotStats.tempMax)">
                  <span class="sa-mini__label">Máxima</span>
                  <span class="sa-mini__value"
                    >{{ formatStatTemp(barnSnapshotStats.tempMax)
                    }}<span v-if="barnSnapshotStats.tempMax != null" class="sa-mini__unit">ºC</span></span
                  >
                  <span class="sa-mini__time">{{ formatSnapshotAt(barnSnapshotStats.tempMaxAt) }}</span>
                </div>
                <div class="sa-mini" :class="statCardToneClass('avg', barnSnapshotStats.tempAvg)">
                  <span class="sa-mini__label">Média</span>
                  <span class="sa-mini__value"
                    >{{ formatStatTemp(barnSnapshotStats.tempAvg)
                    }}<span v-if="barnSnapshotStats.tempAvg != null" class="sa-mini__unit">ºC</span></span
                  >
                  <span class="sa-mini__time">{{ formatSnapshotAt(barnSnapshotStats.tempAvgAt) }}</span>
                </div>
                <div class="sa-mini" :class="statCardToneClass('atual', barnSnapshotStats.tempAtual)">
                  <span class="sa-mini__label">Atual</span>
                  <span class="sa-mini__value"
                    >{{ formatStatTemp(barnSnapshotStats.tempAtual)
                    }}<span v-if="barnSnapshotStats.tempAtual != null" class="sa-mini__unit">ºC</span></span
                  >
                  <span class="sa-mini__time">{{ formatSnapshotAt(barnSnapshotStats.tempAtualAt) }}</span>
                </div>
              </div>
              <p class="sa-snapshot__group-title">Umidade (% UR)</p>
              <div class="sa-snapshot-row">
                <div class="sa-mini" :class="statCardToneClass('min', barnSnapshotStats.humMin)">
                  <span class="sa-mini__label">Mínima</span>
                  <span class="sa-mini__value"
                    >{{ formatStatHum(barnSnapshotStats.humMin)
                    }}<span v-if="barnSnapshotStats.humMin != null" class="sa-mini__unit">%</span></span
                  >
                  <span class="sa-mini__time">{{ formatSnapshotAt(barnSnapshotStats.humMinAt) }}</span>
                </div>
                <div class="sa-mini" :class="statCardToneClass('max', barnSnapshotStats.humMax)">
                  <span class="sa-mini__label">Máxima</span>
                  <span class="sa-mini__value"
                    >{{ formatStatHum(barnSnapshotStats.humMax)
                    }}<span v-if="barnSnapshotStats.humMax != null" class="sa-mini__unit">%</span></span
                  >
                  <span class="sa-mini__time">{{ formatSnapshotAt(barnSnapshotStats.humMaxAt) }}</span>
                </div>
                <div class="sa-mini" :class="statCardToneClass('avg', barnSnapshotStats.humAvg)">
                  <span class="sa-mini__label">Média</span>
                  <span class="sa-mini__value"
                    >{{ formatStatHum(barnSnapshotStats.humAvg)
                    }}<span v-if="barnSnapshotStats.humAvg != null" class="sa-mini__unit">%</span></span
                  >
                  <span class="sa-mini__time">{{ formatSnapshotAt(barnSnapshotStats.humAvgAt) }}</span>
                </div>
                <div class="sa-mini" :class="statCardToneClass('atual', barnSnapshotStats.humAtual)">
                  <span class="sa-mini__label">Atual</span>
                  <span class="sa-mini__value"
                    >{{ formatStatHum(barnSnapshotStats.humAtual)
                    }}<span v-if="barnSnapshotStats.humAtual != null" class="sa-mini__unit">%</span></span
                  >
                  <span class="sa-mini__time">{{ formatSnapshotAt(barnSnapshotStats.humAtualAt) }}</span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="ambienteSubTab === 'sensores'" class="sa-subtab-panel">
            <h3 class="sa-section__title">Sensores</h3>
            <p v-if="sensors.length" class="sa-muted sa-muted--tab-hint">
              Clique num sensor para ver a última leitura, o histórico e os gráficos deste sensor.
            </p>
            <div v-if="sensors.length" class="sa-map-toolbar">
              <button
                type="button"
                class="sa-map-toggle"
                :class="{ 'sa-map-toggle--active': showSensorsMap }"
                @click="toggleSensorsMap"
              >
                {{ showSensorsMap ? "Ocultar mapa" : "Ver mapa" }}
              </button>
              <p v-if="showMapGpsWarning" class="sa-map-toolbar__hint">
                Nenhum sensor com GPS de instalação. Cadastre latitude e longitude na alocação SmartSense do sensor.
              </p>
            </div>
            <SensorsAmbienteMap
              v-if="showSensorsMap && sensors.length"
              :key="mapInstanceKey"
              ref="sensorsMap"
              class="sa-map-panel"
              :markers="sensorMapMarkers"
              :selected-pk="selectedSensorPk"
              @select="onMapSelectSensor"
            />
            <div v-if="allLatestReadingsFailed" class="warning warning--compact">
              <div class="text">
                <h1>Sem dados disponíveis</h1>
                <p>Não há leituras recentes para os sensores deste barracão.</p>
              </div>
            </div>
            <div v-else class="sa-grid sa-grid--selectable">
              <div
                v-for="row in sensorRows"
                :key="row.pk"
                role="button"
                tabindex="0"
                @click="onSensorCardClick(row)"
                @keydown.enter.prevent="onSensorCardClick(row)"
                @keydown.space.prevent="onSensorCardClick(row)"
                :class="sensorSelectableCardClasses(row)"
              >
                <div class="sa-card__head sa-card__head--compact">
                  <span class="sa-card__icon" aria-hidden="true">◉</span>
                  <div class="sa-card__head-text">
                    <h4 class="sa-card__name">{{ row.name }}</h4>
                    <time
                      v-if="!row.error"
                      class="sa-card__datetime sa-card__datetime--inline"
                    >{{ formatSensorCardDatetime(row) }}</time>
                  </div>
                </div>
                <template v-if="row.error">
                  <p class="sa-card__nodata sa-card__nodata--compact">SEM DADOS</p>
                </template>
                <template v-else>
                  <div class="sa-card__grid-compact">
                    <span class="sa-card__kv">
                      <span class="sa-card__kv-k">Temp.</span>
                      <span class="sa-card__kv-v">{{ formatTempLine(row.temperature) }}</span>
                    </span>
                    <span class="sa-card__kv">
                      <span class="sa-card__kv-k">Umid.</span>
                      <span class="sa-card__kv-v sa-card__kv-v--humidity">{{ formatHumidityLine(row.humidity) }}</span>
                    </span>
                    <template v-if="hasSensorCoords(row)">
                      <span class="sa-card__kv">
                        <span class="sa-card__kv-k">Lat.</span>
                        <span class="sa-card__kv-v sa-card__kv-v--coord">{{ formatCoord(row.latitude) }}</span>
                      </span>
                      <span class="sa-card__kv">
                        <span class="sa-card__kv-k">Long.</span>
                        <span class="sa-card__kv-v sa-card__kv-v--coord">{{ formatCoord(row.longitude) }}</span>
                      </span>
                    </template>
                  </div>
                  <a
                    v-if="hasSensorCoords(row)"
                    :href="googleMapsUrl(row.latitude, row.longitude)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="sa-card__maps-link sa-card__maps-link--compact"
                    @click.stop
                  >
                    Google Maps
                  </a>
                </template>
              </div>
            </div>
            <template v-if="selectedSensorPk != null">
              <h3 class="sa-section__title sa-section__title--after-grid">Última leitura</h3>
              <p class="sa-snapshot__context">{{ selectedSensorLabel }}</p>
              <OpenBalenaCard :sensor="selectedSensorObject" />
              <div v-if="subTabSwitching" class="sa-loading-inline">Carregando…</div>
              <div v-else-if="selectedSensorSnapshotStats.hasData" class="sa-snapshot">
                <p class="sa-snapshot__group-title">Temperatura (ºC)</p>
                <div class="sa-snapshot-row">
                  <div class="sa-mini" :class="statCardToneClass('min', selectedSensorSnapshotStats.tempMin)">
                    <span class="sa-mini__label">Mínima</span>
                    <span class="sa-mini__value"
                      >{{ formatStatTemp(selectedSensorSnapshotStats.tempMin)
                      }}<span v-if="selectedSensorSnapshotStats.tempMin != null" class="sa-mini__unit"
                        >ºC</span
                      ></span
                    >
                    <span class="sa-mini__time">{{ formatSnapshotAt(selectedSensorSnapshotStats.tempMinAt) }}</span>
                  </div>
                  <div class="sa-mini" :class="statCardToneClass('max', selectedSensorSnapshotStats.tempMax)">
                    <span class="sa-mini__label">Máxima</span>
                    <span class="sa-mini__value"
                      >{{ formatStatTemp(selectedSensorSnapshotStats.tempMax)
                      }}<span v-if="selectedSensorSnapshotStats.tempMax != null" class="sa-mini__unit"
                        >ºC</span
                      ></span
                    >
                    <span class="sa-mini__time">{{ formatSnapshotAt(selectedSensorSnapshotStats.tempMaxAt) }}</span>
                  </div>
                  <div class="sa-mini" :class="statCardToneClass('avg', selectedSensorSnapshotStats.tempAvg)">
                    <span class="sa-mini__label">Média</span>
                    <span class="sa-mini__value"
                      >{{ formatStatTemp(selectedSensorSnapshotStats.tempAvg)
                      }}<span v-if="selectedSensorSnapshotStats.tempAvg != null" class="sa-mini__unit"
                        >ºC</span
                      ></span
                    >
                    <span class="sa-mini__time">{{ formatSnapshotAt(selectedSensorSnapshotStats.tempAvgAt) }}</span>
                  </div>
                  <div class="sa-mini" :class="statCardToneClass('atual', selectedSensorSnapshotStats.tempAtual)">
                    <span class="sa-mini__label">Atual</span>
                    <span class="sa-mini__value"
                      >{{ formatStatTemp(selectedSensorSnapshotStats.tempAtual)
                      }}<span v-if="selectedSensorSnapshotStats.tempAtual != null" class="sa-mini__unit"
                        >ºC</span
                      ></span
                    >
                    <span class="sa-mini__time">{{ formatSnapshotAt(selectedSensorSnapshotStats.tempAtualAt) }}</span>
                  </div>
                </div>
                <p class="sa-snapshot__group-title">Umidade (% UR)</p>
                <div class="sa-snapshot-row">
                  <div class="sa-mini" :class="statCardToneClass('min', selectedSensorSnapshotStats.humMin)">
                    <span class="sa-mini__label">Mínima</span>
                    <span class="sa-mini__value"
                      >{{ formatStatHum(selectedSensorSnapshotStats.humMin)
                      }}<span v-if="selectedSensorSnapshotStats.humMin != null" class="sa-mini__unit">%</span></span
                    >
                    <span class="sa-mini__time">{{ formatSnapshotAt(selectedSensorSnapshotStats.humMinAt) }}</span>
                  </div>
                  <div class="sa-mini" :class="statCardToneClass('max', selectedSensorSnapshotStats.humMax)">
                    <span class="sa-mini__label">Máxima</span>
                    <span class="sa-mini__value"
                      >{{ formatStatHum(selectedSensorSnapshotStats.humMax)
                      }}<span v-if="selectedSensorSnapshotStats.humMax != null" class="sa-mini__unit">%</span></span
                    >
                    <span class="sa-mini__time">{{ formatSnapshotAt(selectedSensorSnapshotStats.humMaxAt) }}</span>
                  </div>
                  <div class="sa-mini" :class="statCardToneClass('avg', selectedSensorSnapshotStats.humAvg)">
                    <span class="sa-mini__label">Média</span>
                    <span class="sa-mini__value"
                      >{{ formatStatHum(selectedSensorSnapshotStats.humAvg)
                      }}<span v-if="selectedSensorSnapshotStats.humAvg != null" class="sa-mini__unit">%</span></span
                    >
                    <span class="sa-mini__time">{{ formatSnapshotAt(selectedSensorSnapshotStats.humAvgAt) }}</span>
                  </div>
                  <div class="sa-mini" :class="statCardToneClass('atual', selectedSensorSnapshotStats.humAtual)">
                    <span class="sa-mini__label">Atual</span>
                    <span class="sa-mini__value"
                      >{{ formatStatHum(selectedSensorSnapshotStats.humAtual)
                      }}<span v-if="selectedSensorSnapshotStats.humAtual != null" class="sa-mini__unit">%</span></span
                    >
                    <span class="sa-mini__time">{{ formatSnapshotAt(selectedSensorSnapshotStats.humAtualAt) }}</span>
                  </div>
                </div>
              </div>
              <p v-else class="sa-muted sa-muted--below-cards">
                Sem dados agregados para este sensor (relatório vazio ou leitura indisponível).
              </p>
            </template>
          </div>
        </section>

        <section v-if="subTabSwitching" class="sa-section" aria-hidden="true"></section>
        <section v-else-if="!reportBatchPk" class="warning warning--compact">
          <div class="text">
            <h1>Sem dados disponíveis</h1>
            <p>Selecione um lote ativo para ver o histórico e o gráfico do lote.</p>
          </div>
        </section>

        <template v-else>
          <section class="sa-section">
            <h3 class="sa-section__title">{{ historicSectionTitle }}</h3>
            <div
              v-if="reportLoaded && reportRows.length"
              class="sa-date-filter"
            >
              <div class="sa-date-filter__group">
                <span class="sa-date-filter__prefix">De:</span>
                <input
                  v-model="reportFilterStartDraft"
                  type="datetime-local"
                  step="1"
                  class="sa-date-filter__input"
                  :max="reportFilterEndDraft || undefined"
                />
              </div>
              <div class="sa-date-filter__group">
                <span class="sa-date-filter__prefix">Até:</span>
                <input
                  v-model="reportFilterEndDraft"
                  type="datetime-local"
                  step="1"
                  class="sa-date-filter__input"
                  :min="reportFilterStartDraft || undefined"
                />
              </div>
              <button
                type="button"
                class="sa-date-filter__apply"
                :disabled="!reportDateFilterDraftDirty || dateFilterApplying"
                @click="applyReportDateFilterDraft"
              >
                {{ dateFilterApplying ? "Carregando…" : "Buscar" }}
              </button>
              <div class="sa-date-filter__secondary">
                <button
                  type="button"
                  class="sa-date-filter__link"
                  :disabled="dateFilterApplying"
                  @click="resetReportDateFilter"
                >
                  Últimos 7 dias
                </button>
                <span class="sa-date-filter__sep" aria-hidden="true">·</span>
                <button
                  type="button"
                  class="sa-date-filter__link"
                  :disabled="dateFilterApplying"
                  @click="setReportDateFilterFullReport"
                >
                  Período completo
                </button>
                <span v-if="reportDateFilterSummary" class="sa-date-filter__hint">{{ reportDateFilterSummary }}</span>
              </div>
            </div>
            <div v-if="dateFilterApplying" class="sa-loading-inline">Carregando…</div>
            <div v-else-if="!reportLoaded" class="sa-loading-inline">Carregando relatório…</div>
            <div v-else-if="reportError" class="warning warning--compact">
              <div class="text text--error">
                <h1>Sem dados disponíveis</h1>
                <p>{{ reportError }}</p>
              </div>
            </div>
            <div v-else-if="!reportRows.length" class="warning warning--compact">
              <div class="text">
                <h1>Sem dados disponíveis</h1>
                <p>Não há registros no relatório deste lote.</p>
              </div>
            </div>
            <div v-else-if="!reportRowsInDateRange.length" class="warning warning--compact">
              <div class="text">
                <h1>Sem dados no período</h1>
                <p>Não há registros entre as datas selecionadas. Ajuste o intervalo ou use «Período completo».</p>
              </div>
            </div>
            <template v-else>
              <div class="sa-historic-panel">
                <div v-if="activeDailyThermalAmplitude.hasData" class="sa-amp-wrap">
                  <div
                    class="sa-amp-card"
                    :class="amplitudeCardToneClass(activeDailyThermalAmplitude.latest.amplitude)"
                  >
                    <div class="sa-amp-card__top">
                      <div class="sa-amp-card__lead">
                        <p class="sa-amp-card__eyebrow">Relatório do lote</p>
                        <p v-if="ambienteSubTab === 'sensores'" class="sa-amp-card__context">
                          {{ selectedSensorLabel }}
                        </p>
                        <h4 class="sa-amp-card__title">Amplitude térmica diária</h4>
                        <p class="sa-amp-card__day">{{ activeDailyThermalAmplitude.latest.dateLabel }}</p>
                      </div>
                      <div class="sa-amp-card__figure">
                        <p class="sa-amp-card__value">
                          {{ activeDailyThermalAmplitude.latest.amplitude.toFixed(1)
                          }}<span class="sa-amp-card__unit">ºC</span>
                        </p>
                        <p class="sa-amp-card__meta">
                          Mín. {{ activeDailyThermalAmplitude.latest.min.toFixed(1) }} ºC · Máx.
                          {{ activeDailyThermalAmplitude.latest.max.toFixed(1) }} ºC
                        </p>
                      </div>
                    </div>
                    <p v-if="activeDailyThermalAmplitude.days.length > 1" class="sa-amp-card__more-title">
                      Outros dias
                    </p>
                    <ul v-if="activeDailyThermalAmplitude.days.length > 1" class="sa-amp-card__list">
                      <li
                        v-for="d in activeDailyThermalAmplitude.days.slice(1, 8)"
                        :key="'amp-' + ambienteSubTab + '-' + d.dayKey"
                      >
                        <span>{{ d.dateLabel }}</span>
                        <span class="sa-amp-card__list-amp">{{ d.amplitude.toFixed(1) }} ºC</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <p v-if="ambienteSubTab === 'sensores' && !reportRowsForCharts.length" class="sa-muted">
                  <template v-if="sensorHistoricUsesLatestFallback">
                    Não há pontos no relatório deste lote para o sensor selecionado. A tabela abaixo mostra a
                    última leitura disponível para este sensor.
                  </template>
                  <template v-else>
                    Não há pontos no relatório deste lote para o sensor selecionado nem leitura recente para exibir.
                  </template>
                </p>
                <template v-if="reportRowsForCharts.length">
                  <div class="sa-chart-box">
                    <div class="sa-chart-box__inner">
                      <TempHumidityBatchChart
                        metric="temperature"
                        :datasets="chartDatasets"
                        :chart-key="chartRenderKey + chartFilterKey"
                      />
                    </div>
                  </div>

                  <div v-if="chartDatasetsHumidity.length" class="sa-chart-box sa-chart-box--humidity">
                    <div class="sa-chart-box__inner">
                      <TempHumidityBatchChart
                        metric="humidity"
                        :datasets="chartDatasetsHumidity"
                        :chart-key="chartRenderKey + chartFilterKey"
                      />
                    </div>
                  </div>
                </template>

                <div class="sa-table-wrap">
                  <button
                    type="button"
                    class="sa-table-toggle"
                    :aria-expanded="historicTableOpen ? 'true' : 'false'"
                    @click="toggleHistoricTable"
                  >
                    {{ historicTableOpen ? "Ocultar tabela detalhada" : "Ver tabela detalhada" }}
                  </button>
                  <template v-if="historicTableOpen">
                    <div v-if="!historicTableReady" class="sa-loading-inline">Carregando tabela…</div>
                    <table v-else class="sa-table">
                      <thead>
                        <tr>
                          <th class="sa-table__th sa-table__th--datetime" scope="col">Data e hora</th>
                          <th class="sa-table__th sa-table__th--sensor" scope="col">Sensor</th>
                          <th class="sa-table__th sa-table__th--num" scope="col">Temperatura (ºC)</th>
                          <th class="sa-table__th sa-table__th--num" scope="col">Umidade (% UR)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(r, idx) in historicPanelTableRows" :key="'hist-' + ambienteSubTab + '-' + idx">
                          <td class="sa-table__td sa-table__td--datetime">{{ r.datetimeLabel }}</td>
                          <td class="sa-table__td sa-table__td--sensor">
                            <span class="sa-table__sensor-name">{{ r.sensorName }}</span>
                          </td>
                          <td class="sa-table__td sa-table__td--num">{{ r.tempLabel }}</td>
                          <td class="sa-table__td sa-table__td--num">{{ r.humLabel }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </template>
                </div>
              </div>
            </template>
          </section>
        </template>
      </template>
    </template>
  </div>
</template>

<script>
import { addDays, endOfDay, format, parseISO, startOfDay, subDays } from "date-fns"
import { ptBR } from "date-fns/locale"
import {
  SMARTSENSE_SENSOR_TYPE,
  isSmartSenseSensor,
  smartsenseApi,
  smartsenseSensorDisplayName,
} from "../constants/smartsense"
import OpenBalenaCard from "./OpenBalenaCard.vue"
import PageLoader from "./Loader.vue"
import { sensorScreenBlocked } from "../utils/feedInventoryAccess"
import SensorsAmbienteMap from "./SensorsAmbienteMap.vue"
import TempHumidityBatchChart from "./TempHumidityBatchChart.vue"

/** Paletas por métrica: temperatura (tons quentes + contraste) e umidade (água / ar). */
const TEMP_LINE_COLORS = [
  "#e85d04",
  "#e63946",
  "#2a9d8f",
  "#457b9d",
  "#f4a261",
  "#9b5de5",
  "#bc6c25",
]

const HUM_LINE_COLORS = [
  "#0077b6",
  "#06d6a0",
  "#4895ef",
  "#7209b7",
  "#48cae4",
  "#fb8500",
  "#4361ee",
]

export default {
  name: "SmartAmbientePage",
  components: {
    OpenBalenaCard,
    PageLoader,
    SensorsAmbienteMap,
    TempHumidityBatchChart,
  },
  data() {
    return {
      loading: true,
      sensorRows: [],
      /** Roster SmartSense do curral (`sensors/report/home`). */
      homeBarnSensors: [],
      /** Sensores do relatório (`sensors_by_mac`), mesmo sem observações no lote. */
      batchReportSensors: [],
      /** Bundle completo por PK (`allocation` + `sensor`) para GPS de instalação. */
      batchReportBundlesByPk: {},
      reportRows: [],
      reportFilterStart: "",
      reportFilterEnd: "",
      reportFilterStartDraft: "",
      reportFilterEndDraft: "",
      reportLoaded: false,
      reportError: null,
      chartRenderKey: 0,
      chartFilterKey: 0,
      /** Sub-aba: resumo do barracão ou lista de sensores + detalhe filtrado. */
      ambienteSubTab: "resumo",
      /** true durante a troca de sub-aba: mostra loading enquanto os cálculos pesados (stats, gráfico, tabela) rodam. */
      subTabSwitching: false,
      /** true enquanto aplica um novo filtro De/Até: mostra loading enquanto gráfico/tabela recalculam sobre o novo período. */
      dateFilterApplying: false,
      selectedSensorPk: null,
      showSensorsMap: false,
      mapInstanceKey: 0,
      /** Tabela detalhada: fechada por padrão, só monta/computa ao clicar (evita travar a troca de aba). */
      historicTableOpen: false,
      historicTableReady: false,
    }
  },
  computed: {
    barn() {
      return this.$store.getters.getBarnTarget
    },
    batch() {
      return this.$store.getters.getBatchTarget
    },
    /** PK do lote atual (rota / store) para `batch_report/{pk}/`. */
    reportBatchPk() {
      return this.batch?.pk ?? null
    },
    /**
     * Sensores temp/umidade só do cadastro (store), sem merge do relatório.
     * Usado por `findPlatformSensorBySeriesKey` (evita recursão com o computed `sensors`) e
     * por `sensors`. É computed (cacheado) porque varre `getAllBarns` (todos os barracões da
     * conta) e antes era refeito a cada linha do relatório processada — o gargalo real da
     * página de Sensores em lotes longos.
     */
    storeTempHumiditySensors() {
      const barn = this.barn
      if (!barn?.pk) return []
      const byPk = new Map()
      const addFromList = (list) => {
        for (const s of list || []) {
          if (!isSmartSenseSensor(s)) continue
          const pk = s.pk ?? s.id
          if (pk == null) continue
          if (!byPk.has(pk)) byPk.set(pk, { ...s, pk })
        }
      }
      for (const farm of this.$store.getters.getAllBarns || []) {
        const barns = farm.barn || []
        const b = barns.find((x) => x && x.pk === barn.pk)
        if (b?.sensors?.length) addFromList(b.sensors)
      }
      if (Array.isArray(barn.sensors) && barn.sensors.length) addFromList(barn.sensors)
      const cached = this.$store.getters.getCachedHomeBarnData(barn.pk)
      if (cached?.sensors?.length) addFromList(cached.sensors)
      addFromList(this.$store.getters.getBarnTargetSensorsResolved || [])
      return Array.from(byPk.values())
    },
    sensors() {
      const barn = this.barn
      if (!barn?.pk) return []
      const byPk = new Map()
      for (const s of this.homeBarnSensors || []) {
        this.mergeSensorIntoMap(byPk, s)
      }
      for (const s of this.storeTempHumiditySensors) {
        this.mergeSensorIntoMap(byPk, s)
      }
      for (const s of this.batchReportSensors || []) {
        this.mergeSensorIntoMap(byPk, s)
      }

      for (const row of this.reportRows || []) {
        const sid = this.sensorPkFromRow(row)
        if (sid == null) continue
        const platform = this.findPlatformSensorBySeriesKey(sid)
        const pk =
          this.sensorCatalogPk(platform) ??
          (typeof sid === "number" ? sid : /^\d+$/.test(String(sid)) ? Number(sid) : null)
        if (pk == null || byPk.has(String(pk))) continue
        const name =
          this.sensorDisplayNameFromReportRow(row) ||
          (platform && (this.chartLegendNameForPlatformSensor(platform) || platform.name)) ||
          String(pk)
        this.mergeSensorIntoMap(byPk, {
          pk,
          type: SMARTSENSE_SENSOR_TYPE,
          name,
        })
      }

      return Array.from(byPk.values()).sort((a, b) => {
        const na = String(a?.name ?? a?.pk ?? "").toLowerCase()
        const nb = String(b?.name ?? b?.pk ?? "").toLowerCase()
        if (na !== nb) return na.localeCompare(nb, undefined, { numeric: true })
        return Number(a.pk) - Number(b.pk)
      })
    },
    /** Nome na plataforma por PK do sensor ou por MAC (várias chaves para bater com o relatório). */
    sensorDisplayNameMap() {
      const m = new Map()
      const put = (key, name) => {
        if (key == null || key === "") return
        const str = String(key).trim()
        if (!str) return
        m.set(str, name)
        const normMac = this.normalizeMacKey(str)
        if (normMac) {
          m.set(normMac, name)
          const compact = normMac.replace(/:/g, "")
          if (compact) m.set(compact, name)
        }
      }
      for (const s of this.sensors) {
        const name =
          this.chartLegendNameForPlatformSensor(s) ||
          this.fallbackSensorLabel(this.extractMacFromSensor(s) || s.pk)
        put(s.pk, name)
        put(String(s.pk), name)
        if (s.id != null && s.id !== "") {
          put(s.id, name)
          put(String(s.id), name)
        }
        const mac = this.extractMacFromSensor(s)
        if (mac) put(mac, name)
      }
      return m
    },
    reportFilterRangeMs() {
      let start = null
      let end = null
      if (this.reportFilterStart) {
        const d = new Date(this.reportFilterStart)
        if (!Number.isNaN(d.getTime())) start = d.getTime()
      }
      if (this.reportFilterEnd) {
        const d = new Date(this.reportFilterEnd)
        if (!Number.isNaN(d.getTime())) end = d.getTime()
      }
      return { start, end }
    },
    /** Relatório recortado pelo intervalo De/Até (histórico, gráficos, amplitude). */
    reportRowsInDateRange() {
      const rows = this.reportRows || []
      const { start, end } = this.reportFilterRangeMs
      if (start == null && end == null) return rows
      return rows.filter((r) => {
        const ms = this.parseDateMs(r)
        if (ms == null) return false
        if (start != null && ms < start) return false
        if (end != null && ms > end) return false
        return true
      })
    },
    reportDateFilterDraftDirty() {
      return (
        this.reportFilterStartDraft !== this.reportFilterStart ||
        this.reportFilterEndDraft !== this.reportFilterEnd
      )
    },
    reportDateFilterSummary() {
      const n = this.reportRowsInDateRange.length
      const total = (this.reportRows || []).length
      if (!total || n === total) return null
      return `${n} de ${total} leituras no período`
    },
    /** Linhas do relatório usadas nos gráficos/tabela (período + sensor na aba Sensores). */
    reportRowsForCharts() {
      let rows = this.reportRowsInDateRange
      if (this.ambienteSubTab === "sensores" && this.selectedSensorPk != null) {
        rows = rows.filter((r) => this.rowBelongsToSensor(r, this.selectedSensorPk))
      }
      // Filtro de estabilidade: no máx. 4 leituras/hora por sensor. Evita que lotes
      // longos travem os gráficos e a tabela com milhares de pontos redundantes.
      return this.filterRowsToMaxPerHourBySensor(rows, 4)
    },
    selectedSensorLabel() {
      const s = this.sensors.find(
        (x) => this.sensorCatalogPk(x) === this.selectedSensorPk,
      )
      return (s && this.chartLegendNameForPlatformSensor(s)) || "Sensor"
    },
    /** Objeto completo do sensor selecionado na aba Sensores (para o card Open Balena). */
    selectedSensorObject() {
      return this.sensors.find(
        (x) => this.sensorCatalogPk(x) === this.selectedSensorPk,
      )
    },
    /** Subtítulo da página: sensores do curral ou sensor selecionado na aba Sensores. */
    pageSensorSubtitle() {
      if (!this.sensors.length) return null
      if (this.ambienteSubTab === "sensores" && this.selectedSensorLabel) {
        return this.selectedSensorLabel
      }
      const names = this.sensors
        .map((s) => this.chartLegendNameForPlatformSensor(s))
        .filter(Boolean)
      if (!names.length) return null
      if (names.length === 1) return names[0]
      return names.join(" · ")
    },
    chartDatasets() {
      return this.buildLineDatasetsFromReport("temperature", this.reportRowsForCharts)
    },
    chartDatasetsHumidity() {
      return this.buildLineDatasetsFromReport("humidity", this.reportRowsForCharts)
    },
    allLatestReadingsFailed() {
      if (!this.sensors.length || !this.sensorRows.length) return false
      return this.sensorRows.every((r) => r.error)
    },
    /** Agrega últimas leituras (mín / máx / média / mais recente) a partir de linhas do relatório e/ou leituras da API. */
    barnSnapshotStats() {
      return this.computeSnapshotStatsForContext(this.reportRows || [], null)
    },
    /** Mesmo conjunto de estatísticas do resumo, restrito ao sensor selecionado (aba Sensores). */
    selectedSensorSnapshotStats() {
      const pk = this.selectedSensorPk
      if (pk == null) return this.emptyBarnSnapshot()
      const filtered = (this.reportRows || []).filter((r) => this.rowBelongsToSensor(r, pk))
      const row = (this.sensorRows || []).find((r) => r.pk === pk)
      const apiOnly = row ? [row] : []
      return this.computeSnapshotStatsForContext(filtered, apiOnly)
    },
    tableRows() {
      return this.buildTableRowsFromReport(this.reportRowsForCharts)
    },
    /** Por dia civil (local): hoje = 00:00 até agora; dias passados = 24 h civis. Máx − mín nos pontos do relatório. */
    dailyThermalAmplitude() {
      return this.buildDailyThermalAmplitudeFromRows(this.reportRowsInDateRange)
    },
    /** Amplitude diária só com pontos do sensor selecionado. */
    selectedSensorDailyThermalAmplitude() {
      if (this.selectedSensorPk == null) return { hasData: false, days: [], latest: null }
      const filtered = this.reportRowsInDateRange.filter((r) =>
        this.rowBelongsToSensor(r, this.selectedSensorPk),
      )
      return this.buildDailyThermalAmplitudeFromRows(filtered)
    },
    /** Tabela na aba Sensores: relatório filtrado ou, se vazio, última leitura do sensor (API). */
    sensorHistoricTableRows() {
      const fromReport = this.buildTableRowsFromReport(this.reportRowsForCharts)
      if (fromReport.length) return fromReport
      if (this.ambienteSubTab !== "sensores" || this.selectedSensorPk == null) return []
      const row = (this.sensorRows || []).find((r) => r.pk === this.selectedSensorPk)
      if (!row || row.error) return []
      const dt = row.readAt instanceof Date && !Number.isNaN(row.readAt.getTime()) ? row.readAt : null
      const datetimeLabel = dt ? format(dt, "dd/MM/yyyy HH:mm:ss", { locale: ptBR }) : "—"
      const name =
        (row.name && String(row.name).trim()) || this.selectedSensorLabel || "Sensor"
      const temp = row.temperature != null ? Number(row.temperature) : null
      const hum = row.humidity != null ? Number(row.humidity) : null
      const hasT = temp != null && !Number.isNaN(temp)
      const hasH = hum != null && !Number.isNaN(hum)
      if (!hasT && !hasH) return []
      return [
        {
          _ts: dt ? dt.getTime() : 0,
          datetimeLabel,
          sensorName: name,
          tempLabel: hasT ? `${temp.toFixed(2)} ºC` : "—",
          humLabel: hasH ? `${hum.toFixed(1)} % UR` : "—",
        },
      ]
    },
    sensorHistoricUsesLatestFallback() {
      return !this.reportRowsForCharts.length && this.sensorHistoricTableRows.length > 0
    },
    /** Amplitude diária: lote inteiro (Resumo) ou só o sensor selecionado (Sensores). */
    activeDailyThermalAmplitude() {
      return this.ambienteSubTab === "resumo"
        ? this.dailyThermalAmplitude
        : this.selectedSensorDailyThermalAmplitude
    },
    /** Título da secção de histórico alinhado ao mesmo relatório, com recorte por sensor na aba Sensores. */
    historicSectionTitle() {
      return this.ambienteSubTab === "resumo"
        ? "Histórico do lote (relatório)"
        : `Histórico do lote (relatório) — ${this.selectedSensorLabel}`
    },
    /** Linhas da tabela de histórico: todas (Resumo) ou filtradas / fallback (Sensores). */
    historicPanelTableRows() {
      return this.ambienteSubTab === "resumo" ? this.tableRows : this.sensorHistoricTableRows
    },
    sensorMapMarkers() {
      return this.sensors
        .map((sensor) => {
          const sensorPk = this.sensorCatalogPk(sensor)
          const resolved = this.resolveSensorCoordinates(sensor)
          if (!resolved) return null
          const reading = (this.sensorRows || []).find(
            (r) => r.pk === sensorPk || String(r.pk) === String(sensorPk),
          )
          return {
            pk: sensorPk,
            name: this.chartLegendNameForPlatformSensor(sensor) || sensor.name,
            lat: resolved.lat,
            lng: resolved.lng,
            hasGps: true,
            temperature: reading?.temperature,
            humidity: reading?.humidity,
            readAt: reading?.readAt,
            error: reading?.error ?? false,
          }
        })
        .filter(Boolean)
    },
    /** Aviso só quando há sensores mas nenhum ponto válido no mapa. */
    showMapGpsWarning() {
      if (!this.showSensorsMap || !this.sensors.length) return false
      return this.sensorMapMarkers.length === 0
    },
  },
  watch: {
    showSensorsMap(open) {
      if (!open) return
      this.$nextTick(() => {
        const map = this.$refs.sensorsMap
        if (map && map.map) map.map.invalidateSize()
      })
    },
    barn: {
      handler() {
        this.reloadAll()
      },
      deep: true,
    },
    batch: {
      handler() {
        this.reloadAll()
      },
      deep: true,
    },
    sensors: {
      handler(newVal, oldVal) {
        const sig = (v) =>
          (v || [])
            .map((s) => (s && s.pk != null ? String(s.pk) : ""))
            .filter(Boolean)
            .sort()
            .join(",")
        if (sig(newVal) === sig(oldVal)) return
        // O cadastro de sensores mudou: o sensor resolvido em cache por linha pode ter ficado obsoleto.
        this._sensorMatchCache = new WeakMap()
        this.ensureSelectedSensor()
        if (!this.loading && this.reportLoaded) {
          this.fetchLatestPerSensor()
        }
      },
      immediate: true,
    },
    ambienteSubTab(tab) {
      if (tab === "sensores") this.ensureSelectedSensor()
      this.updateDocumentTitle()
      this.closeHistoricTable()
    },
    sensorRows() {
      this.ensureSelectedSensor()
    },
    pageSensorSubtitle() {
      this.updateDocumentTitle()
    },
    selectedSensorPk() {
      this.updateDocumentTitle()
      this.closeHistoricTable()
    },
    reportFilterStart() {
      this.chartFilterKey += 1
      this.closeHistoricTable()
    },
    reportFilterEnd() {
      this.chartFilterKey += 1
      this.closeHistoricTable()
    },
  },
  created() {
    if (sensorScreenBlocked(this.$store.getters.getCurrentUser, this.$store.getters.getCompanySettings)) {
      this.loading = false
      return
    }
    /** Cache não-reativo de `datetimeFromRow` por linha: o mesmo objeto tem sua data reparseada
     * dezenas de vezes (stats, amplitude, tabela, gráficos) a cada troca de aba/sensor/período. */
    this._dtCache = new WeakMap()
    /** Cache não-reativo do sensor resolvido por linha (ver `resolvedSensorPkForRow`). */
    this._sensorMatchCache = new WeakMap()
    this.updateDocumentTitle()
    this.reloadAll()
  },
  mounted() {
    this.updateDocumentTitle()
  },
  methods: {
    updateDocumentTitle() {
      const base = "PecSmart - SmartSense"
      const sub = this.pageSensorSubtitle
      document.title = sub ? `${sub} · ${base}` : base
    },
    /** 7 dias civis terminando amanhã (hoje + 1), inclusive. */
    defaultReportDateFilterRange() {
      const tomorrow = addDays(startOfDay(new Date()), 1)
      return {
        start: startOfDay(subDays(tomorrow, 6)),
        end: endOfDay(tomorrow),
      }
    },
    applyReportDateFilterRange(startDate, endDate) {
      this.reportFilterStart = format(startDate, "yyyy-MM-dd'T'HH:mm")
      this.reportFilterEnd = format(endDate, "yyyy-MM-dd'T'HH:mm")
      this.reportFilterStartDraft = this.reportFilterStart
      this.reportFilterEndDraft = this.reportFilterEnd
    },
    /**
     * Roda `fn` só depois do loading pintar na tela: mudar o filtro De/Até dispara, na mesma
     * tick, o recálculo síncrono de amplitude/gráfico/tabela sobre o período inteiro.
     */
    runWithDateFilterLoading(fn) {
      if (this.dateFilterApplying) return
      this.dateFilterApplying = true
      this.closeHistoricTable()
      this.$nextTick(() => {
        setTimeout(() => {
          fn()
          this.$nextTick(() => {
            this.dateFilterApplying = false
          })
        }, 20)
      })
    },
    /** Aplica as datas digitadas (botão "Buscar"), evitando recalcular o relatório a cada tecla digitada. */
    applyReportDateFilterDraft() {
      this.runWithDateFilterLoading(() => {
        this.reportFilterStart = this.reportFilterStartDraft
        this.reportFilterEnd = this.reportFilterEndDraft
      })
    },
    initReportDateFilterBounds() {
      const { start, end } = this.defaultReportDateFilterRange()
      this.applyReportDateFilterRange(start, end)
    },
    setReportDateFilterFullReport() {
      this.runWithDateFilterLoading(() => {
        const rows = this.reportRows || []
        let minMs = Infinity
        let maxMs = -Infinity
        for (const r of rows) {
          const ms = this.parseDateMs(r)
          if (ms == null) continue
          if (ms < minMs) minMs = ms
          if (ms > maxMs) maxMs = ms
        }
        if (!Number.isFinite(minMs)) {
          this.reportFilterStart = ""
          this.reportFilterEnd = ""
          this.reportFilterStartDraft = ""
          this.reportFilterEndDraft = ""
          return
        }
        this.applyReportDateFilterRange(new Date(minMs), new Date(maxMs))
        this.chartFilterKey += 1
      })
    },
    resetReportDateFilter() {
      this.runWithDateFilterLoading(() => {
        this.initReportDateFilterBounds()
        this.chartFilterKey += 1
      })
    },
    formatTempLine(v) {
      if (v == null || Number.isNaN(Number(v))) return "— ºC"
      return `${Number(v).toFixed(1)} ºC`
    },
    formatHumidityLine(v) {
      if (v == null || Number.isNaN(Number(v))) return "— % UR"
      return `${Number(v).toFixed(1)}% UR`
    },
    formatStatTemp(n) {
      if (n == null || Number.isNaN(Number(n))) return "—"
      return `${Number(n).toFixed(1)}`
    },
    formatStatHum(n) {
      if (n == null || Number.isNaN(Number(n))) return "—"
      return `${Number(n).toFixed(1)}`
    },
    buildTableRowsFromReport(rows) {
      if (!rows || !rows.length) return []
      // O nome depende só do sensor (poucos por lote), não da linha — resolvê-lo de novo a
      // cada linha (busca em `sensors`/store) é o desperdício que mais pesa em lotes longos.
      const nameCache = new Map()
      const nameForSid = (sid) => {
        if (sid == null) return null
        const key = String(sid)
        if (nameCache.has(key)) return nameCache.get(key)
        const name = this.sensorNameForSeriesKey(sid)
        nameCache.set(key, name)
        return name
      }
      return [...rows]
        .map((r) => {
          const sid = this.sensorPkFromRow(r)
          const dt = this.datetimeFromRow(r)
          const temp = r.temperature != null ? Number(r.temperature) : null
          const hum = r.humidity != null ? Number(r.humidity) : null
          return {
            _ts: dt ? dt.getTime() : 0,
            datetimeLabel: dt ? format(dt, "dd/MM/yyyy HH:mm:ss", { locale: ptBR }) : "—",
            sensorName: nameForSid(sid) || this.sensorDisplayNameFromReportRow(r) || "—",
            tempLabel: temp != null && !Number.isNaN(temp) ? `${temp.toFixed(2)} ºC` : "—",
            humLabel: hum != null && !Number.isNaN(hum) ? `${hum.toFixed(1)} % UR` : "—",
          }
        })
        .sort((a, b) => b._ts - a._ts)
    },
    /**
     * Sensor "dono" de uma linha do relatório, cacheado por linha (`_sensorMatchCache`).
     * Resolver isso é caro (MAC/label matching) — cachear por linha em vez de repetir a busca
     * a cada `rowBelongsToSensor` (3+ varreduras completas do relatório por troca de sensor,
     * e de novo a cada vez que o usuário troca de sensor) é o que torna a aba Sensores rápida
     * já na segunda troca, e evita reproduzir o mesmo matching 3x na primeira.
     */
    resolvedSensorPkForRow(row) {
      const cache = this._sensorMatchCache
      if (cache.has(row)) return cache.get(row)
      const resolved = this.resolveSensorPkForRowUncached(row)
      cache.set(row, resolved)
      return resolved
    },
    resolveSensorPkForRowUncached(row) {
      const sid = this.sensorPkFromRow(row)
      const sensors = this.sensors || []
      if (sid != null) {
        for (const sensor of sensors) {
          const cpk = this.sensorCatalogPk(sensor)
          if (cpk === sid || String(cpk) === String(sid)) return cpk
        }
        for (const sensor of sensors) {
          const mac = this.extractMacFromSensor(sensor)
          if (!mac) continue
          const nSid = this.normalizeMacKey(String(sid))
          const nMac = this.normalizeMacKey(mac)
          if (nSid && nMac && nSid === nMac) return this.sensorCatalogPk(sensor)
          const cSid = String(sid).replace(/:/g, "").toUpperCase()
          const cMac = mac.replace(/:/g, "").toUpperCase()
          if (cSid === cMac) return this.sensorCatalogPk(sensor)
        }
        const rowSensor = this.findPlatformSensorBySeriesKey(sid)
        if (rowSensor) {
          const match = sensors.find(
            (s) => this.sensorCatalogPk(s) === rowSensor.pk || s.pk === rowSensor.pk,
          )
          if (match) return this.sensorCatalogPk(match)
        }
      }
      for (const sensor of sensors) {
        if (this.reportRowMatchesSensorLabel(row, sensor)) return this.sensorCatalogPk(sensor)
      }
      return sid
    },
    rowBelongsToSensor(row, sensorPk) {
      if (sensorPk == null) return true
      const resolved = this.resolvedSensorPkForRow(row)
      if (resolved == null) return false
      return resolved === sensorPk || String(resolved) === String(sensorPk)
    },
    /** Associa observação ao sensor pela etiqueta quando MAC/PK do relatório ≠ cadastro. */
    reportRowMatchesSensorLabel(row, platformSensor) {
      if (!row || !platformSensor) return false
      const wantRaw =
        this.chartLegendNameForPlatformSensor(platformSensor) ||
        this.resolvedPlatformSensorName(platformSensor) ||
        (platformSensor.name != null ? String(platformSensor.name).trim() : "")
      if (!wantRaw) return false
      const want = wantRaw.trim().toLowerCase()
      const pushCand = (out, raw) => {
        if (raw == null || String(raw).trim() === "") return
        const t = String(raw).trim()
        if (this.looksLikeMacAddress(t)) return
        out.add(t.toLowerCase())
      }
      const cand = new Set()
      pushCand(cand, row.report_sensor_label)
      pushCand(cand, row.sensor_name)
      pushCand(cand, row.sensorName)
      pushCand(cand, row.sensor_display_name)
      pushCand(cand, row.smartsense_sensor_name)
      pushCand(cand, row.temp_humidity_sensor_name)
      if (typeof row.sensor === "object" && row.sensor !== null) {
        pushCand(cand, row.sensor.name)
        pushCand(cand, row.sensor.sensor_name)
      }
      const alloc = row.allocation
      if (alloc && typeof alloc === "object") {
        pushCand(cand, alloc.sensor_name)
        pushCand(cand, alloc.name)
        pushCand(cand, alloc.label)
      }
      for (const c of cand) {
        if (c === want) return true
      }
      const disp = this.sensorDisplayNameFromReportRow(row)
      if (
        disp &&
        !this.looksLikeMacAddress(disp.trim()) &&
        disp.trim().toLowerCase() === want
      ) {
        return true
      }
      return false
    },
    matchesSensorPk(a, b) {
      if (a == null || b == null) return false
      return a === b || String(a) === String(b)
    },
    ensureSelectedSensor() {
      const rows = this.sensorRows || []
      const catalog = this.sensors || []
      if (!catalog.length && !rows.length) {
        this.selectedSensorPk = null
        return
      }
      if (this.selectedSensorPk != null) {
        const inCatalog = catalog.some((s) =>
          this.matchesSensorPk(this.sensorCatalogPk(s), this.selectedSensorPk),
        )
        const inRows = rows.some((r) => this.matchesSensorPk(r.pk, this.selectedSensorPk))
        if (inCatalog || inRows) return
      }
      const firstWithData = rows.find((r) => !r.error && r.pk != null)
      if (firstWithData) {
        this.selectedSensorPk = firstWithData.pk
        return
      }
      const firstRow = rows.find((r) => r.pk != null)
      if (firstRow) {
        this.selectedSensorPk = firstRow.pk
        return
      }
      this.selectedSensorPk = this.sensorCatalogPk(catalog[0])
    },
    /**
     * Roda `fn` só depois do loading pintar na tela: trocar de aba/sensor dispara, na mesma
     * tick, o recálculo síncrono de stats/amplitude/gráfico/tabela sobre o relatório inteiro.
     * O restante da UI (abas, cards de sensores) já muda na hora — só o bloco "Última leitura"
     * e o histórico ficam com loading.
     */
    runHeavyRecompute(fn) {
      if (this.subTabSwitching) return
      this.subTabSwitching = true
      this.$nextTick(() => {
        setTimeout(() => {
          fn()
          this.$nextTick(() => {
            this.subTabSwitching = false
          })
        }, 20)
      })
    },
    selectSensor(pk) {
      if (pk == null || pk === this.selectedSensorPk) return
      this.closeHistoricTable()
      this.runHeavyRecompute(() => {
        this.selectedSensorPk = pk
        this.chartFilterKey += 1
      })
    },
    onSensorCardClick(row) {
      if (!row || row.error) return
      this.selectSensor(row.pk)
    },
    selectSubTab(tab) {
      if (tab === this.ambienteSubTab) return
      this.closeHistoricTable()
      this.runHeavyRecompute(() => {
        this.ambienteSubTab = tab
      })
    },
    closeHistoricTable() {
      this.historicTableOpen = false
      this.historicTableReady = false
    },
    toggleHistoricTable() {
      if (this.historicTableOpen) {
        this.closeHistoricTable()
        return
      }
      this.historicTableOpen = true
      this.historicTableReady = false
      // Deixa o "Carregando…" pintar antes de montar a tabela (cálculo síncrono pesado em lotes longos).
      this.$nextTick(() => {
        setTimeout(() => {
          this.historicTableReady = true
        }, 30)
      })
    },
    toggleSensorsMap() {
      this.showSensorsMap = !this.showSensorsMap
      if (this.showSensorsMap) this.mapInstanceKey += 1
    },
    onMapSelectSensor(pk) {
      if (pk == null) return
      this.selectSensor(pk)
    },
    pickNumericField(source, keys) {
      if (!source || typeof source !== "object") return null
      for (const k of keys) {
        const v = source[k]
        if (v != null && v !== "" && !Number.isNaN(Number(v))) return Number(v)
      }
      return null
    },
    normalizeLatLngPair(lat, lng) {
      if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) return null
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null
      return { lat, lng, hasGps: true }
    },
    /** Corrige latitude/longitude invertidos (comum em cadastros BR). */
    shouldSwapLatLngFields(lat, lng) {
      if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) return false
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false
      if (Math.abs(lat) > 90 || Math.abs(lng) > 90) return false
      return Math.abs(lat) > 35 && Math.abs(lng) <= 35
    },
    normalizeLatLngPairAfterSwap(lat, lng) {
      if (lat == null || lng == null) return null
      let latN = Number(lat)
      let lngN = Number(lng)
      if (Number.isNaN(latN) || Number.isNaN(lngN)) return null
      if (this.shouldSwapLatLngFields(latN, lngN)) {
        const tmp = latN
        latN = lngN
        lngN = tmp
      }
      return this.normalizeLatLngPair(latN, lngN)
    },
    inferLatLngFromPair(a, b) {
      const aN = Number(a)
      const bN = Number(b)
      if (Number.isNaN(aN) || Number.isNaN(bN)) return null
      if (aN >= -90 && aN <= 90 && (bN < -90 || bN > 90)) {
        return this.normalizeLatLngPairAfterSwap(aN, bN)
      }
      if (bN >= -90 && bN <= 90 && (aN < -90 || aN > 90)) {
        return this.normalizeLatLngPairAfterSwap(bN, aN)
      }
      if (aN >= -90 && aN <= 90 && bN >= -180 && bN <= 180) {
        if (Math.abs(aN) > Math.abs(bN)) return this.normalizeLatLngPairAfterSwap(bN, aN)
        return this.normalizeLatLngPairAfterSwap(aN, bN)
      }
      return null
    },
    parseCoordinateValue(raw) {
      if (raw == null || raw === "") return null
      if (typeof raw === "object" && !Array.isArray(raw)) {
        return (
          this.coordsFromAllocation(raw) ||
          this.coordsFromExplicitPosition(raw)
        )
      }
      if (Array.isArray(raw) && raw.length >= 2) {
        return this.inferLatLngFromPair(raw[0], raw[1])
      }
      const text = String(raw).trim()
      if (!text) return null
      const parts = text.split(/[,;|\s]+/).filter(Boolean).map((p) => Number(p))
      if (parts.length >= 2 && !parts.some((n) => Number.isNaN(n))) {
        return this.inferLatLngFromPair(parts[0], parts[1])
      }
      return null
    },
    /** GPS da alocação SmartSense (`smartsense_sensor_allocation`). */
    coordsFromAllocation(alloc) {
      if (!alloc || typeof alloc !== "object") return null
      const lat = this.pickNumericField(alloc, ["latitude", "lat", "latitud"])
      const lng = this.pickNumericField(alloc, ["longitude", "lng", "lon", "long", "longitud"])
      const direct = this.normalizeLatLngPairAfterSwap(lat, lng)
      if (direct) return direct
      return this.parseCoordinateValue(alloc.location || alloc.coordinates || alloc.geo)
    },
    /** GPS do cadastro do sensor (sem usar coordenadas do barracão/fazenda). */
    coordsFromExplicitPosition(obj) {
      if (!obj || typeof obj !== "object") return null
      const lat = this.pickNumericField(obj, [
        "latitude",
        "lat",
        "latitud",
        "sensor_latitude",
        "geo_lat",
      ])
      const lng = this.pickNumericField(obj, [
        "longitude",
        "lng",
        "lon",
        "long",
        "longitud",
        "sensor_longitude",
        "geo_lng",
        "geo_lon",
      ])
      const direct = this.normalizeLatLngPairAfterSwap(lat, lng)
      if (direct) return direct
      for (const key of ["coordinates", "coord", "gps", "geo", "latlng", "lat_lng", "geopoint", "location"]) {
        const parsed = this.parseCoordinateValue(obj[key])
        if (parsed) return parsed
      }
      return null
    },
    coordsFromSensorObject(obj) {
      if (!obj || typeof obj !== "object") return null
      const alloc =
        obj.allocation && typeof obj.allocation === "object" ? obj.allocation : null
      if (alloc) {
        const fromAlloc = this.coordsFromAllocation(alloc)
        if (fromAlloc) return fromAlloc
      }
      const latKeys = ["latitude", "lat", "latitud", "barn_lat", "farm_lat"]
      const lngKeys = ["longitude", "lng", "lon", "long", "longitud", "barn_lng", "farm_lng"]
      const lat = this.pickNumericField(obj, latKeys)
      const lng = this.pickNumericField(obj, lngKeys)
      const direct = this.normalizeLatLngPairAfterSwap(lat, lng)
      if (direct) return direct
      for (const key of ["coordinates", "coord", "gps", "geo", "location"]) {
        const parsed = this.parseCoordinateValue(obj[key])
        if (parsed) return parsed
      }
      const farm = obj.farm && typeof obj.farm === "object" ? obj.farm : null
      if (farm) return this.coordsFromSensorObject(farm)
      return null
    },
    resolveSensorCoordinates(sensor) {
      if (!sensor) return null
      const pk = this.sensorCatalogPk(sensor)

      let coords = this.coordsFromAllocation(sensor.allocation)
      if (coords) return coords

      coords = this.coordsFromExplicitPosition(sensor)
      if (coords) return coords

      if (pk != null) {
        const bundle = this.batchReportBundlesByPk[String(pk)]
        if (bundle) {
          coords = this.coordsFromAllocation(bundle.allocation)
          if (coords) return coords
        }
      }

      for (const row of this.reportRows || []) {
        if (!this.rowBelongsToSensor(row, pk)) continue
        coords = this.coordsFromAllocation(row.allocation)
        if (coords) return coords
      }

      const fromStore = this.storeTempHumiditySensors.find(
        (s) => this.sensorCatalogPk(s) === pk,
      )
      if (fromStore) {
        coords =
          this.coordsFromAllocation(fromStore.allocation) ||
          this.coordsFromExplicitPosition(fromStore)
        if (coords) return coords
      }

      return null
    },
    resolveMapCenter() {
      const barn = this.barn
      const fromBarn =
        this.coordsFromSensorObject(barn) ||
        this.coordsFromSensorObject(barn?.farm) ||
        this.coordsFromSensorObject(barn?.farm?.farm)
      if (fromBarn) return { lat: fromBarn.lat, lng: fromBarn.lng }
      for (const sensor of this.sensors) {
        const c = this.resolveSensorCoordinates(sensor)
        if (c) return { lat: c.lat, lng: c.lng }
      }
      return { lat: -15.13, lng: -53.19 }
    },
    fallbackCoordsForIndex(index, total, center) {
      const safeTotal = Math.max(total, 1)
      const angle = (2 * Math.PI * index) / safeTotal
      const radius = safeTotal <= 1 ? 0 : 0.0018
      return {
        lat: center.lat + radius * Math.sin(angle),
        lng: center.lng + radius * Math.cos(angle),
        hasGps: false,
      }
    },
    formatSensorCardDatetime(row) {
      if (!row || row.error || !(row.readAt instanceof Date) || Number.isNaN(row.readAt.getTime())) return "—"
      return format(row.readAt, "dd/MM/yyyy HH:mm:ss", { locale: ptBR })
    },
    /**
     * Pontos já ordenados por `x`. Mesmo timestamp: mantém a última leitura (evita segmentos verticais artefactuais).
     */
    dedupeSortedChartPointsByX(pts) {
      if (!pts || pts.length <= 1) return pts || []
      const out = [pts[0]]
      for (let i = 1; i < pts.length; i++) {
        const p = pts[i]
        const last = out[out.length - 1]
        if (p.x === last.x) out[out.length - 1] = p
        else out.push(p)
      }
      return out
    },
    /**
     * Filtro de estabilidade: limita a no máx. `maxPerHour` linhas por hora, por sensor,
     * amostrando uniformemente dentro de cada hora. Aplicado antes da montagem da tabela e
     * dos gráficos, para que lotes longos (milhares de leituras) não travem a página inteira —
     * não afeta estatísticas/amplitude térmica, que continuam usando o relatório completo.
     */
    filterRowsToMaxPerHourBySensor(rows, maxPerHour = 4) {
      if (!rows || rows.length <= maxPerHour) return rows || []
      const HOUR_MS = 60 * 60 * 1000
      const bySensor = new Map()
      for (const r of rows) {
        const t = this.parseDateMs(r)
        if (t == null) continue
        const sid = this.sensorPkFromRow(r)
        const key = sid != null ? String(sid) : "__unknown__"
        if (!bySensor.has(key)) bySensor.set(key, [])
        bySensor.get(key).push(r)
      }
      const out = []
      for (const group of bySensor.values()) {
        group.sort((a, b) => this.parseDateMs(a) - this.parseDateMs(b))
        const buckets = new Map()
        for (const r of group) {
          const bucketKey = Math.floor(this.parseDateMs(r) / HOUR_MS)
          if (!buckets.has(bucketKey)) buckets.set(bucketKey, [])
          buckets.get(bucketKey).push(r)
        }
        for (const bucket of buckets.values()) {
          if (bucket.length <= maxPerHour) {
            out.push(...bucket)
            continue
          }
          const step = bucket.length / maxPerHour
          for (let i = 0; i < maxPerHour; i++) {
            out.push(bucket[Math.min(bucket.length - 1, Math.floor(i * step))])
          }
        }
      }
      return out
    },
    buildLineDatasetsFromReport(yField, sourceRows) {
      const rows = sourceRows != null ? sourceRows : this.reportRows
      if (!rows.length) return []
      const bySensor = new Map()
      for (const r of rows) {
        const sid = this.sensorPkFromRow(r)
        if (sid == null) continue
        const t = this.parseDateMs(r)
        if (t == null) continue
        const temp = r.temperature != null ? Number(r.temperature) : NaN
        const hum = r.humidity != null ? Number(r.humidity) : NaN
        if (yField === "temperature") {
          if (Number.isNaN(temp)) continue
          const humOk = !Number.isNaN(hum)
          if (!bySensor.has(sid)) bySensor.set(sid, [])
          bySensor.get(sid).push({
            x: t,
            y: temp,
            hum: humOk ? hum : null,
          })
        } else {
          if (Number.isNaN(hum)) continue
          const tempOk = !Number.isNaN(temp)
          if (!bySensor.has(sid)) bySensor.set(sid, [])
          bySensor.get(sid).push({
            x: t,
            y: hum,
            temp: tempOk ? temp : null,
          })
        }
      }
      let i = 0
      const out = []
      const palette = yField === "temperature" ? TEMP_LINE_COLORS : HUM_LINE_COLORS
      for (const [pk, pts] of bySensor) {
        pts.sort((a, b) => a.x - b.x)
        const data = this.dedupeSortedChartPointsByX(pts)
        const baseName = this.sensorNameForSeriesKey(pk)
        const label =
          yField === "temperature" ? `${baseName} · temperatura` : `${baseName} · umidade`
        const color = palette[i % palette.length]
        out.push({
          label,
          data,
          borderColor: color,
          backgroundColor: "transparent",
          borderWidth: 2.5,
          fill: false,
          /** Curvas com tensão > 0 no eixo temporal costumam “voltar” no tempo (Bézier); linha reta reflete a série. */
          lineTension: 0,
          pointRadius: 3.5,
          pointHoverRadius: 6,
          pointBackgroundColor: "#ffffff",
          pointBorderColor: color,
          pointBorderWidth: 2,
          pointHoverBackgroundColor: color,
          pointHoverBorderColor: "#ffffff",
          pointHoverBorderWidth: 2,
          spanGaps: false,
        })
        i++
      }
      return out
    },
    emptyBarnSnapshot() {
      return {
        hasData: false,
        tempMin: null,
        tempMax: null,
        tempAvg: null,
        tempAtual: null,
        tempMinAt: null,
        tempMaxAt: null,
        tempAvgAt: null,
        tempAtualAt: null,
        humMin: null,
        humMax: null,
        humAvg: null,
        humAtual: null,
        humMinAt: null,
        humMaxAt: null,
        humAvgAt: null,
        humAtualAt: null,
      }
    },
    meanArr(arr) {
      return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null
    },
    /**
     * `Math.min(...arr)`/`Math.max(...arr)` passam o array inteiro como argumentos —
     * em lotes longos (dezenas de milhares de leituras) isso é lento e pode estourar a
     * pilha de chamadas. Um reduce simples é O(n) e não tem esse limite.
     */
    minOf(arr) {
      return arr.length ? arr.reduce((a, b) => (b < a ? b : a), arr[0]) : null
    },
    maxOf(arr) {
      return arr.length ? arr.reduce((a, b) => (b > a ? b : a), arr[0]) : null
    },
    formatSnapshotAt(dt) {
      if (!(dt instanceof Date) || Number.isNaN(dt.getTime())) return "—"
      return format(dt, "dd/MM/yyyy HH:mm", { locale: ptBR })
    },
    /** `field`: `temp` ou `hum`. Mín = primeira ocorrência no tempo; máx = pico mais recente. */
    pickMinMaxTimes(points, field) {
      const withVal = points.filter((p) => p[field] != null && !Number.isNaN(Number(p[field])))
      if (!withVal.length) return { minAt: null, maxAt: null }
      const minV = this.minOf(withVal.map((p) => Number(p[field])))
      const maxV = this.maxOf(withVal.map((p) => Number(p[field])))
      const mins = withVal.filter((p) => Number(p[field]) === minV).sort((a, b) => a.ts - b.ts)
      const maxs = withVal.filter((p) => Number(p[field]) === maxV).sort((a, b) => b.ts - a.ts)
      return { minAt: mins[0]?.dt ?? null, maxAt: maxs[0]?.dt ?? null }
    },
    pickClosestToMeanTime(points, field, meanVal) {
      if (meanVal == null || Number.isNaN(Number(meanVal))) return null
      let best = null
      let bestD = Infinity
      for (const p of points) {
        const v = p[field]
        if (v == null || Number.isNaN(Number(v))) continue
        const d = Math.abs(Number(v) - meanVal)
        if (d < bestD || (d === bestD && p.ts < (best?.ts ?? Infinity))) {
          bestD = d
          best = p
        }
      }
      return best?.dt ?? null
    },
    /**
     * Estatísticas T/U: com `reportRows` não vazio e valores válidos, usa esses pontos.
     * Caso contrário, usa `sensorRowsOverride` se fornecido (ex.: um sensor), senão todas as linhas em `sensorRows`.
     */
    computeSnapshotStatsForContext(reportRowsInput, sensorRowsOverride) {
      const mean = (arr) => this.meanArr(arr)
      const reportRows = Array.isArray(reportRowsInput) ? reportRowsInput : []
      const apiPool =
        sensorRowsOverride != null
          ? (Array.isArray(sensorRowsOverride) ? sensorRowsOverride : [sensorRowsOverride]).filter(Boolean)
          : this.sensorRows || []

      if (reportRows.length) {
        const temps = []
        const hums = []
        const points = []
        for (const r of reportRows) {
          const dt = this.datetimeFromRow(r)
          if (!dt || Number.isNaN(dt.getTime())) continue
          const ts = dt.getTime()
          const temp = r.temperature != null ? Number(r.temperature) : NaN
          const hum = r.humidity != null ? Number(r.humidity) : NaN
          const tOk = !Number.isNaN(temp)
          const hOk = !Number.isNaN(hum)
          if (tOk) temps.push(temp)
          if (hOk) hums.push(hum)
          if (tOk || hOk) {
            points.push({
              ts,
              dt,
              temp: tOk ? temp : null,
              hum: hOk ? hum : null,
            })
          }
        }
        if (temps.length || hums.length) {
          const tempMin = temps.length ? this.minOf(temps) : null
          const tempMax = temps.length ? this.maxOf(temps) : null
          const tempAvg = temps.length ? mean(temps) : null
          const humMin = hums.length ? this.minOf(hums) : null
          const humMax = hums.length ? this.maxOf(hums) : null
          const humAvg = hums.length ? mean(hums) : null

          const { minAt: tempMinAt, maxAt: tempMaxAt } = this.pickMinMaxTimes(points, "temp")
          const { minAt: humMinAt, maxAt: humMaxAt } = this.pickMinMaxTimes(points, "hum")
          const tempAvgAt = this.pickClosestToMeanTime(points, "temp", tempAvg)
          const humAvgAt = this.pickClosestToMeanTime(points, "hum", humAvg)

          let tempAtual = null
          let humAtual = null
          let tempAtualAt = null
          let humAtualAt = null
          if (points.length) {
            const maxTs = this.maxOf(points.map((x) => x.ts))
            const maxSec = Math.floor(maxTs / 1000)
            const latestBucket = points.filter((x) => Math.floor(x.ts / 1000) === maxSec)
            const tVals = latestBucket.map((x) => x.temp).filter((v) => v != null && !Number.isNaN(v))
            const hVals = latestBucket.map((x) => x.hum).filter((v) => v != null && !Number.isNaN(v))
            tempAtual = tVals.length ? mean(tVals) : temps.length ? mean(temps) : null
            humAtual = hVals.length ? mean(hVals) : hums.length ? mean(hums) : null
            tempAtualAt = new Date(maxTs)
            humAtualAt = new Date(maxTs)
          } else {
            tempAtual = temps.length ? mean(temps) : null
            humAtual = hums.length ? mean(hums) : null
          }
          return {
            hasData: true,
            tempMin,
            tempMax,
            tempAvg,
            tempAtual,
            tempMinAt,
            tempMaxAt,
            tempAvgAt,
            tempAtualAt,
            humMin,
            humMax,
            humAvg,
            humAtual,
            humMinAt,
            humMaxAt,
            humAvgAt,
            humAtualAt,
          }
        }
      }

      const rows = apiPool.filter(
        (r) => !r.error && r.temperature != null && !Number.isNaN(Number(r.temperature)),
      )
      const rowsH = apiPool.filter(
        (r) => !r.error && r.humidity != null && !Number.isNaN(Number(r.humidity)),
      )
      const temps = rows.map((r) => Number(r.temperature))
      const hums = rowsH.map((r) => Number(r.humidity))
      if (!temps.length && !hums.length) return this.emptyBarnSnapshot()

      const sPoints = []
      for (const r of apiPool) {
        if (r.error || !(r.readAt instanceof Date) || Number.isNaN(r.readAt.getTime())) continue
        const temp = r.temperature != null ? Number(r.temperature) : NaN
        const hum = r.humidity != null ? Number(r.humidity) : NaN
        const tOk = !Number.isNaN(temp)
        const hOk = !Number.isNaN(hum)
        if (!tOk && !hOk) continue
        sPoints.push({
          ts: r.readAt.getTime(),
          dt: r.readAt,
          temp: tOk ? temp : null,
          hum: hOk ? hum : null,
        })
      }

      const tempMin = temps.length ? this.minOf(temps) : null
      const tempMax = temps.length ? this.maxOf(temps) : null
      const tempAvg = temps.length ? mean(temps) : null
      const humMin = hums.length ? this.minOf(hums) : null
      const humMax = hums.length ? this.maxOf(hums) : null
      const humAvg = hums.length ? mean(hums) : null

      let tempMinAt = null
      let tempMaxAt = null
      let tempAvgAt = null
      let humMinAt = null
      let humMaxAt = null
      let humAvgAt = null
      if (sPoints.length) {
        const tm = this.pickMinMaxTimes(sPoints, "temp")
        const hm = this.pickMinMaxTimes(sPoints, "hum")
        tempMinAt = tm.minAt
        tempMaxAt = tm.maxAt
        humMinAt = hm.minAt
        humMaxAt = hm.maxAt
        tempAvgAt = this.pickClosestToMeanTime(sPoints, "temp", tempAvg)
        humAvgAt = this.pickClosestToMeanTime(sPoints, "hum", humAvg)
      }

      const withTs = apiPool.filter(
        (r) => !r.error && r.readAt instanceof Date && !Number.isNaN(r.readAt.getTime()),
      )
      let tempAtual = null
      let humAtual = null
      let tempAtualAt = null
      let humAtualAt = null
      if (withTs.length) {
        const latest = [...withTs].sort((a, b) => b.readAt - a.readAt)[0]
        tempAtual =
          latest.temperature != null && !Number.isNaN(Number(latest.temperature))
            ? Number(latest.temperature)
            : temps.length
              ? mean(temps)
              : null
        humAtual =
          latest.humidity != null && !Number.isNaN(Number(latest.humidity))
            ? Number(latest.humidity)
            : hums.length
              ? mean(hums)
              : null
        tempAtualAt = latest.readAt
        humAtualAt = latest.readAt
      } else {
        tempAtual = temps.length ? mean(temps) : null
        humAtual = hums.length ? mean(hums) : null
      }
      return {
        hasData: true,
        tempMin,
        tempMax,
        tempAvg,
        tempAtual,
        tempMinAt,
        tempMaxAt,
        tempAvgAt,
        tempAtualAt,
        humMin,
        humMax,
        humAvg,
        humAtual,
        humMinAt,
        humMaxAt,
        humAvgAt,
        humAtualAt,
      }
    },
    /**
     * Amplitude por dia civil (timezone local): no dia atual, só pontos entre 00:00 e o instante
     * atual; em dias já encerrados, entre 00:00 e o fim do dia (24 h civis).
     */
    buildDailyThermalAmplitudeFromRows(rows) {
      if (!rows || !rows.length) {
        return { hasData: false, days: [], latest: null }
      }
      const now = new Date()
      const nowMs = now.getTime()
      const todayKey = format(now, "yyyy-MM-dd")

      const byDay = new Map()
      for (const r of rows) {
        const dt = this.datetimeFromRow(r)
        if (!dt || Number.isNaN(dt.getTime())) continue
        const temp = r.temperature != null ? Number(r.temperature) : NaN
        if (Number.isNaN(temp)) continue
        const dayKey = format(dt, "yyyy-MM-dd")
        const dayStart = startOfDay(dt)
        const windowEndMs = dayKey === todayKey ? nowMs : endOfDay(dt).getTime()
        const t = dt.getTime()
        if (t < dayStart.getTime() || t > windowEndMs) continue

        if (!byDay.has(dayKey)) {
          byDay.set(dayKey, {
            dayKey,
            dateLabel: format(dayStart, "dd/MM/yyyy", { locale: ptBR }),
            temps: [],
          })
        }
        byDay.get(dayKey).temps.push(temp)
      }
      const days = [...byDay.values()]
        .map((d) => {
          const min = this.minOf(d.temps)
          const max = this.maxOf(d.temps)
          return {
            dayKey: d.dayKey,
            dateLabel: d.dateLabel,
            min,
            max,
            amplitude: max - min,
          }
        })
        .sort((a, b) => b.dayKey.localeCompare(a.dayKey))
      if (!days.length) return { hasData: false, days: [], latest: null }
      return { hasData: true, days, latest: days[0] }
    },
    /** Tom dos mini-cards «Última leitura» por função (igual para temp. e umidade). */
    statCardToneClass(kind, value) {
      if (value == null || Number.isNaN(Number(value))) return ""
      if (kind === "min") return "sa-mini--stat-min"
      if (kind === "max") return "sa-mini--stat-max"
      if (kind === "avg") return "sa-mini--stat-avg"
      if (kind === "atual") return "sa-mini--stat-atual"
      return "sa-tone--neutral"
    },
    /** Tom visual dos cards de sensor na grelha (pela temperatura). */
    tempCardToneClass(celsius) {
      if (celsius == null || Number.isNaN(Number(celsius))) return "sa-tone--neutral"
      const t = Number(celsius)
      if (t < 18) return "sa-tone--temp-cold"
      if (t < 21) return "sa-tone--temp-cool"
      if (t < 24) return "sa-tone--temp-mild"
      if (t < 27) return "sa-tone--temp-warm"
      return "sa-tone--temp-hot"
    },
    sensorCardClassList(row) {
      if (row.error) return ["sa-card", "sa-card--empty"]
      const tone = this.tempCardToneClass(row.temperature)
      return ["sa-card", "sa-card--filled", tone]
    },
    sensorSelectableCardClasses(row) {
      const base = this.sensorCardClassList(row)
      const hasSelection = this.selectedSensorPk != null
      const isSelected = hasSelection && this.matchesSensorPk(this.selectedSensorPk, row.pk)
      const isDimmed = hasSelection && !row.error && !isSelected
      return base.concat([
        "sa-card--selectable",
        { "sa-card--selected": isSelected },
        { "sa-card--dimmed": isDimmed },
      ])
    },
    amplitudeCardToneClass(amp) {
      if (amp == null || Number.isNaN(Number(amp))) return "sa-tone--neutral"
      const a = Number(amp)
      if (a < 3) return "sa-tone--amp-low"
      if (a < 6) return "sa-tone--amp-mid"
      return "sa-tone--amp-high"
    },
    sensorCatalogPk(sensor) {
      if (!sensor || typeof sensor !== "object") return null
      const v = sensor.pk ?? sensor.id
      if (v == null || v === "") return null
      const n = Number(v)
      return Number.isNaN(n) ? String(v).trim() : n
    },
    mergeSensorIntoMap(byPk, sensor) {
      const pk = this.sensorCatalogPk(sensor)
      if (pk == null) return
      const key = String(pk)
      const prev = byPk.get(key)
      const merged = prev
        ? { ...prev, ...sensor, pk: this.sensorCatalogPk(prev) ?? pk }
        : { ...sensor, pk }
      const name =
        smartsenseSensorDisplayName(merged) ||
        smartsenseSensorDisplayName(sensor) ||
        smartsenseSensorDisplayName(prev)
      if (name) merged.name = name
      byPk.set(key, merged)
    },
    extractSensorsFromBatchReport(json) {
      if (json == null || typeof json !== "object") return []
      const byMacRoot = json.sensors_by_mac ?? (json.data && json.data.sensors_by_mac)
      if (!byMacRoot || typeof byMacRoot !== "object" || Array.isArray(byMacRoot)) return []
      const out = []
      const seen = new Set()
      for (const bundle of Object.values(byMacRoot)) {
        if (!bundle || typeof bundle !== "object") continue
        const s = bundle.sensor
        if (!s || typeof s !== "object") continue
        const pk = this.sensorCatalogPk(s)
        if (pk == null || seen.has(String(pk))) continue
        seen.add(String(pk))
        const bundleLabel =
          (typeof bundle.sensor_name === "string" && bundle.sensor_name.trim()) ||
          (typeof bundle.name === "string" && bundle.name.trim()) ||
          null
        const name = smartsenseSensorDisplayName(s) || bundleLabel
        out.push({
          ...s,
          pk,
          ...(name ? { name } : {}),
          allocation:
            bundle.allocation && typeof bundle.allocation === "object"
              ? bundle.allocation
              : s.allocation,
        })
      }
      return out
    },
    extractBatchBundlesByPk(json) {
      if (json == null || typeof json !== "object") return {}
      const byMacRoot = json.sensors_by_mac ?? (json.data && json.data.sensors_by_mac)
      if (!byMacRoot || typeof byMacRoot !== "object" || Array.isArray(byMacRoot)) return {}
      const map = {}
      for (const bundle of Object.values(byMacRoot)) {
        if (!bundle || typeof bundle !== "object") continue
        const pk = this.sensorCatalogPk(bundle.sensor)
        if (pk == null) continue
        map[String(pk)] = bundle
      }
      return map
    },
    latestReportRowForSensor(sensor) {
      const pk = this.sensorCatalogPk(sensor)
      if (pk == null) return null
      let best = null
      let bestMs = -1
      for (const row of this.reportRows || []) {
        if (!this.rowBelongsToSensor(row, pk)) continue
        const ms = this.parseDateMs(row)
        if (ms == null || ms <= bestMs) continue
        bestMs = ms
        best = row
      }
      return best
    },
    sensorCoordsFields(sensor) {
      const coords = this.resolveSensorCoordinates(sensor)
      if (!coords) return {}
      return { latitude: coords.lat, longitude: coords.lng }
    },
    hasSensorCoords(row) {
      if (!row) return false
      const lat = Number(row.latitude)
      const lng = Number(row.longitude)
      return Number.isFinite(lat) && Number.isFinite(lng)
    },
    formatCoord(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return "—"
      return n.toFixed(6)
    },
    googleMapsUrl(lat, lng) {
      const la = Number(lat)
      const ln = Number(lng)
      if (!Number.isFinite(la) || !Number.isFinite(ln)) return "#"
      const q = `${la},${ln}`
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`
    },
    sensorRowFromReport(sensor, reportRow) {
      const labelFor = (s) => this.chartLegendNameForPlatformSensor(s) || s.name
      return {
        pk: this.sensorCatalogPk(sensor),
        name: labelFor(sensor),
        temperature: reportRow.temperature,
        humidity: reportRow.humidity,
        readAt: this.datetimeFromRow(reportRow),
        error: false,
        ...this.sensorCoordsFields(sensor),
      }
    },
    parseRowReadAt(json) {
      if (!json || typeof json !== "object") return null
      const raw =
        json.datetime_created ?? json.created_at ?? json.datetime ?? json.timestamp ?? json.date
      if (raw == null) return null
      try {
        const d = typeof raw === "string" ? parseISO(raw) : new Date(raw)
        return Number.isNaN(d.getTime()) ? null : d
      } catch {
        return null
      }
    },
    normalizeMacKey(mac) {
      if (mac == null || mac === "") return ""
      let s = String(mac).trim().toUpperCase()
      s = s.replace(/-/g, ":")
      return s
    },
    looksLikeMacAddress(str) {
      if (!str || typeof str !== "string") return false
      const s = str.trim()
      if (/^([0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}$/i.test(s)) return true
      const hex = s.replace(/[^0-9A-Fa-f]/gi, "")
      return hex.length === 12 && /^[0-9A-Fa-f]{12}$/i.test(hex)
    },
    /** Nome em gráficos / UI: prioriza a chave `name` do cadastro (ex.: "teste-luis"), não PK/MAC. */
    chartLegendNameForPlatformSensor(sensor) {
      return smartsenseSensorDisplayName(sensor) || this.resolvedPlatformSensorName(sensor)
    },
    /** Nome legível do cadastro do sensor (vários campos possíveis na API). */
    resolvedPlatformSensorName(sensor) {
      return smartsenseSensorDisplayName(sensor)
    },
    /**
     * Sensores temp/umidade só do cadastro (store), sem merge do relatório.
     * Usado por findPlatformSensorBySeriesKey para evitar recursão com o computed `sensors`.
     */
    findPlatformSensorBySeriesKey(sid) {
      if (sid == null && sid !== 0) return null
      const list = this.storeTempHumiditySensors
      for (const s of list) {
        if (s.pk != null && (s.pk === sid || String(s.pk) === String(sid))) return s
        if (s.id != null && (s.id === sid || String(s.id) === String(sid))) return s
        const mac = this.extractMacFromSensor(s)
        if (!mac) continue
        const nMac = this.normalizeMacKey(mac)
        const nSid = this.normalizeMacKey(String(sid))
        if (nMac && nSid && nMac === nSid) return s
        const cMac = nMac.replace(/:/g, "")
        const cSid = String(sid).replace(/[^0-9A-Fa-f]/gi, "")
        if (cMac && cSid && cMac.toUpperCase() === cSid.toUpperCase()) return s
      }
      return null
    },
    extractMacFromSensor(sensor) {
      if (!sensor || typeof sensor !== "object") return null
      const raw =
        sensor.mac ??
        sensor.mac_address ??
        sensor.sensor_mac ??
        sensor.device_mac ??
        sensor.dev_eui ??
        sensor.deveui ??
        sensor.lora_deveui ??
        sensor.lorawan_dev_eui ??
        sensor.hardware_eui ??
        sensor.ieee_address ??
        (sensor.data &&
          (sensor.data.mac ??
            sensor.data.mac_address ??
            sensor.data.sensor_mac ??
            sensor.data.dev_eui ??
            sensor.data.deveui))
      if (raw == null || raw === "") return null
      const n = this.normalizeMacKey(raw)
      return n || String(raw).trim()
    },
    resolveSensorDisplayName(key) {
      if (key == null && key !== 0) return null
      const map = this.sensorDisplayNameMap
      const s = String(key).trim()
      if (map.has(s)) return map.get(s)
      const norm = this.normalizeMacKey(s)
      if (norm && map.has(norm)) return map.get(norm)
      const compact = norm ? norm.replace(/:/g, "") : s.replace(/:/g, "").toUpperCase()
      if (compact && map.has(compact)) return map.get(compact)
      const num = Number(key)
      if (!Number.isNaN(num) && map.has(String(num))) return map.get(String(num))
      return null
    },
    fallbackSensorLabel(key) {
      if (key == null && key !== 0) return "—"
      const s = String(key)
      if (/[a-fA-F0-9]{2}:+[a-fA-F0-9]{2}/.test(s) || s.includes(":")) {
        const hex = s.replace(/[^a-fA-F0-9]/gi, "")
        const tail = hex.length >= 4 ? hex.slice(-4) : hex
        return tail ? `Sensor ·${tail}` : "Sensor"
      }
      return `Sensor ${key}`
    },
    /** Nome amigável enviado no relatório (prioridade) ou cadastro da plataforma. */
    sensorDisplayNameFromReportRow(row) {
      if (!row || typeof row !== "object") return null
      if (row.report_sensor_label != null && String(row.report_sensor_label).trim() !== "") {
        return String(row.report_sensor_label).trim()
      }
      const alloc = row.allocation && typeof row.allocation === "object" ? row.allocation : null
      const raw =
        row.sensor_display_name ??
        row.sensor_name ??
        row.sensorName ??
        row.sensor_label ??
        row.device_name ??
        row.smartsense_sensor_name ??
        row.temp_humidity_sensor_name ??
        row.name ??
        alloc?.sensor_name ??
        alloc?.sensor_display_name ??
        alloc?.name ??
        alloc?.label ??
        alloc?.title
      if (raw == null || String(raw).trim() === "") {
        const sid = this.sensorPkFromRow(row)
        if (sid != null) {
          const fromPlatform = this.resolveSensorDisplayName(sid)
          if (fromPlatform) return fromPlatform
        }
        return null
      }
      const trimmed = String(raw).trim()
      if (this.looksLikeMacAddress(trimmed)) {
        const sid = this.sensorPkFromRow(row)
        if (sid != null) {
          const fromPlatform = this.resolveSensorDisplayName(sid)
          if (fromPlatform) return fromPlatform
        }
      }
      return trimmed
    },
    rowMatchesSensorKey(row, sid) {
      const k = this.sensorPkFromRow(row)
      if (k == null || sid == null) return false
      if (k === sid) return true
      const nk = this.normalizeMacKey(String(k))
      const ns = this.normalizeMacKey(String(sid))
      if (nk && ns && nk === ns) return true
      return String(k) === String(sid)
    },
    sensorNameForSeriesKey(sid) {
      const platform = this.findPlatformSensorBySeriesKey(sid)
      const fromPlatform = platform && this.chartLegendNameForPlatformSensor(platform)
      if (fromPlatform) return fromPlatform
      for (const r of this.reportRows) {
        if (!this.rowMatchesSensorKey(r, sid)) continue
        const fromData = this.sensorDisplayNameFromReportRow(r)
        if (fromData) return fromData
      }
      return this.resolveSensorDisplayName(sid) || this.fallbackSensorLabel(sid)
    },
    sensorPkFromRow(row) {
      if (!row || typeof row !== "object") return null
      const nestedSensor =
        row.sensor && typeof row.sensor === "object"
          ? row.sensor.pk ?? row.sensor.id
          : null
      const v =
        nestedSensor ??
        (typeof row.sensor === "number" || typeof row.sensor === "string" ? row.sensor : null) ??
        row.sensor_pk ??
        row.smartsense_sensor ??
        row.temp_humidity_sensor ??
        row.sensor_id ??
        row.smartsense_sensor_id ??
        row.smartsense_sensor_pk ??
        row.temp_humidity_sensor_id ??
        row.temp_humidity_sensor_pk
      if (v != null && v !== "") {
        const n = Number(v)
        return Number.isNaN(n) ? String(v).trim() : n
      }
      if (row.mac != null && row.mac !== "") {
        const m = this.normalizeMacKey(row.mac)
        return m || String(row.mac).trim()
      }
      return null
    },
    datetimeFromRow(row) {
      const cache = this._dtCache
      const cacheable = row != null && typeof row === "object"
      if (cacheable && cache.has(row)) return cache.get(row)
      const dt = this.parseDatetimeFromRowUncached(row)
      if (cacheable) cache.set(row, dt)
      return dt
    },
    parseDatetimeFromRowUncached(row) {
      const raw =
        row.datetime_created ??
        row.created_at ??
        row.datetime ??
        row.read_at ??
        row.reading_at ??
        row.measured_at ??
        row.observed_at ??
        row.timestamp ??
        row.date ??
        row.time
      if (raw == null) return null
      try {
        return typeof raw === "string" ? parseISO(raw) : new Date(raw)
      } catch {
        return null
      }
    },
    parseDateMs(row) {
      const d = this.datetimeFromRow(row)
      return d && !Number.isNaN(d.getTime()) ? d.getTime() : null
    },
    extractReportRows(json) {
      if (json == null || typeof json !== "object") return []
      const byMacRoot = json.sensors_by_mac ?? (json.data && json.data.sensors_by_mac)
      if (
        byMacRoot != null &&
        typeof byMacRoot === "object" &&
        !Array.isArray(byMacRoot)
      ) {
        return this.flattenSensorsByMacReport(byMacRoot)
      }
      return []
    },
    /** Une `observations` de cada MAC em linhas com `sensor` = MAC normalizada (para série e legenda). */
    flattenSensorsByMacReport(sensorsByMac) {
      const merged = []
      for (const [macKey, bundle] of Object.entries(sensorsByMac)) {
        if (!bundle || typeof bundle !== "object") continue
        const obs = bundle.observations
        if (!Array.isArray(obs)) continue
        const keyFromEntry = this.normalizeMacKey(macKey) || String(macKey).trim()
        const alloc =
          bundle.allocation && typeof bundle.allocation === "object" ? bundle.allocation : {}
        for (const item of obs) {
          if (!item || typeof item !== "object") continue
          const fromObs = item.mac != null ? this.normalizeMacKey(item.mac) : ""
          const sensorMacKey = (fromObs || keyFromEntry || "").trim()
          let sensorIdentity = sensorMacKey || keyFromEntry
          const bs = bundle.sensor
          if (bs && typeof bs === "object" && bs.pk != null && String(bs.pk).trim() !== "") {
            const pn = Number(bs.pk)
            sensorIdentity = Number.isNaN(pn) ? bs.pk : pn
          }
          const itemAlloc = item.allocation && typeof item.allocation === "object" ? item.allocation : {}
          const mergedAlloc = { ...alloc, ...itemAlloc }
          const baseRow = {
            ...item,
            allocation: mergedAlloc,
            sensor: sensorIdentity ?? sensorMacKey ?? keyFromEntry,
          }
          const lookupKey = sensorMacKey || keyFromEntry
          const sidStr = String(sensorIdentity ?? "")
          const identityIsPk =
            typeof sensorIdentity === "number" || (sidStr.trim() !== "" && /^\d+$/.test(sidStr.trim()))
          if (!identityIsPk && lookupKey) {
            const platSensor = this.findPlatformSensorBySeriesKey(lookupKey)
            if (platSensor != null && platSensor.pk != null) {
              const pkNum = Number(platSensor.pk)
              baseRow.sensor = Number.isNaN(pkNum) ? platSensor.pk : pkNum
            }
          }
          let label = this.sensorDisplayNameFromReportRow(baseRow)
          if (!label && bs && typeof bs === "object") {
            const bn = bs.name ?? bs.sensor_name ?? bs.sensorName
            if (bn != null && String(bn).trim() !== "") label = String(bn).trim()
          }
          if (!label && typeof bundle.sensor_name === "string" && bundle.sensor_name.trim()) {
            label = bundle.sensor_name.trim()
          }
          if (!label && typeof bundle.name === "string" && bundle.name.trim()) {
            label = bundle.name.trim()
          }
          if (!label) {
            label =
              this.resolveSensorDisplayName(sensorMacKey) ||
              this.resolveSensorDisplayName(keyFromEntry) ||
              this.resolveSensorDisplayName(macKey) ||
              null
          }
          if (label) baseRow.report_sensor_label = label
          merged.push(baseRow)
        }
      }
      return merged
    },
    /**
     * Converte objeto do batch_report que traz apenas `observations` (+ opcionalmente `sensor` / mac)
     * no formato esperado pelo flatten MAC.
     */
    reportRowsFromObservationsBundle(root) {
      if (
        !root ||
        typeof root !== "object" ||
        Array.isArray(root) ||
        !Array.isArray(root.observations) ||
        !root.observations.length
      ) {
        return []
      }
      const macCand =
        root.mac ?? root.sensor_mac ?? root.device_mac ?? (root.sensor && root.sensor.mac)
      let wrapKey =
        this.normalizeMacKey(macCand) || (macCand ? String(macCand).trim() : "")
      if (!wrapKey && root.sensor?.pk != null) wrapKey = `sensor_pk:${root.sensor.pk}`
      if (!wrapKey) wrapKey = "batch_observations"
      const bundle = {
        observations: root.observations,
        ...(typeof root.sensor_name === "string" && root.sensor_name.trim()
          ? { sensor_name: root.sensor_name.trim() }
          : {}),
        ...(root.sensor ? { sensor: root.sensor } : {}),
        ...(root.allocation && typeof root.allocation === "object" ? { allocation: root.allocation } : {}),
      }
      if (typeof root.name === "string" && root.name.trim()) bundle.name = root.name.trim()
      return this.flattenSensorsByMacReport({ [wrapKey]: bundle })
    },
    async fetchHomeBarnSensorsRoster() {
      const barnPk = this.barn?.pk
      if (!barnPk) return
      try {
        this.$store.commit("incrementActiveRequests")
        const res = await fetch(smartsenseApi.barnSensorsHome(barnPk), {
          headers: this.$store.getters.getHeader,
        })
        if (!res.ok) return
        const json = await res.json()
        if (!Array.isArray(json)) return
        this.homeBarnSensors = json
          .filter(isSmartSenseSensor)
          .map((s) => ({ ...s, pk: s.pk ?? s.id }))
          .filter((s) => s.pk != null)
        this.$store.commit("setHomeBarnDataCache", {
          barnPk,
          sensors: json,
          lastUpdated: new Date().toISOString(),
        })
      } catch {
        /* mantém dados já em cache */
      } finally {
        this.$store.commit("decrementActiveRequests")
      }
    },
    async fetchLatestPerSensor() {
      if (!this.sensors.length) {
        this.sensorRows = []
        return
      }
      const labelFor = (sensor) =>
        this.chartLegendNameForPlatformSensor(sensor) || sensor.name
      const errorRow = (sensor, extra = {}) => ({
        pk: this.sensorCatalogPk(sensor),
        name: labelFor(sensor),
        error: true,
        ...this.sensorCoordsFields(sensor),
        ...extra,
      })

      const rows = []
      const needsApi = []
      for (const sensor of this.sensors) {
        const reportRow = this.latestReportRowForSensor(sensor)
        if (reportRow) {
          rows.push(this.sensorRowFromReport(sensor, reportRow))
        } else {
          needsApi.push(sensor)
        }
      }

      if (!needsApi.length) {
        this.sensorRows = rows
        return
      }

      const anchorPk = this.sensorCatalogPk(needsApi[0])
      if (anchorPk == null) {
        this.sensorRows = [...rows, ...needsApi.map((s) => errorRow(s))]
        return
      }

      let aggregated = null
      let noOpenBatch = false
      let fetchFailed = true
      try {
        this.$store.commit("incrementActiveRequests")
        const res = await fetch(smartsenseApi.lastData(anchorPk), {
          headers: this.$store.getters.getHeader,
        })
        if (res.status === 409) {
          noOpenBatch = true
          fetchFailed = false
        } else if (res.ok) {
          fetchFailed = false
          const json = await res.json()
          if (json != null && typeof json === "object") aggregated = json
        }
      } catch {
        /* mantém fetchFailed */
      } finally {
        this.$store.commit("decrementActiveRequests")
      }

      if (noOpenBatch || fetchFailed || !aggregated) {
        this.sensorRows = [
          ...rows,
          ...needsApi.map((s) => errorRow(s, noOpenBatch ? { noOpenBatch: true } : {})),
        ]
        return
      }

      const readAt = this.parseRowReadAt(aggregated)
      for (const sensor of needsApi) {
        rows.push({
          pk: this.sensorCatalogPk(sensor),
          name: labelFor(sensor),
          temperature: aggregated.temperature,
          humidity: aggregated.humidity,
          readAt,
          error: false,
          ...this.sensorCoordsFields(sensor),
        })
      }
      this.sensorRows = rows
    },
    async fetchBatchReport() {
      this.reportError = null
      this.reportLoaded = false
      this.reportRows = []
      const pk = this.reportBatchPk
      if (!pk) {
        this.reportLoaded = true
        return
      }
      try {
        this.$store.commit("incrementActiveRequests")
        const url = smartsenseApi.batchReport(pk)
        const res = await fetch(url, { headers: this.$store.getters.getHeader })
        if (!res.ok) {
          this.reportError = `Não foi possível carregar o relatório (${res.status}).`
          return
        }
        const json = await res.json()
        this.batchReportBundlesByPk = this.extractBatchBundlesByPk(json)
        this.batchReportSensors = this.extractSensorsFromBatchReport(json)
        this.reportRows = this.extractReportRows(json)
        this.initReportDateFilterBounds()
        this.chartRenderKey += 1
      } catch (e) {
        this.reportError = "Erro ao carregar o relatório do lote."
      } finally {
        this.reportLoaded = true
        this.$store.commit("decrementActiveRequests")
      }
    },
    async reloadAll() {
      this.loading = true
      this.reportLoaded = false
      this.reportError = null
      this.reportRows = []
      this.reportFilterStart = ""
      this.reportFilterEnd = ""
      this.homeBarnSensors = []
      this.batchReportSensors = []
      this.batchReportBundlesByPk = {}
      this.selectedSensorPk = null
      try {
        await this.fetchHomeBarnSensorsRoster()
        await this.fetchBatchReport()
        await this.fetchLatestPerSensor()
      } finally {
        this.loading = false
        this.ensureSelectedSensor()
      }
    },
  },
}
</script>

<style scoped>
.smart-ambiente {
  max-width: 1100px;
  margin: 24px auto 48px;
  padding: 0 16px;
}

.sa-page-head {
  margin-bottom: 16px;
}

.sa-page-head__brand {
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #1b4238;
}

.sa-page-head__sensor {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #2d6a4f;
}

.sa-section {
  margin-bottom: 32px;
}

.sa-section__title {
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 600;
  color: #1b4238;
}

.sa-date-filter {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 4px 8px;
  margin-bottom: 20px;
  margin-top: 8px;
}

.sa-date-filter__group {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 4px 8px;
}

.sa-date-filter__prefix {
  font-size: 14px;
  font-weight: 500;
  color: #1b4238;
  white-space: nowrap;
}

.sa-date-filter__input {
  margin-left: 12px;
  min-width: 200px;
  max-width: 220px;
  padding: 4px 8px;
  font-size: 14px;
  border: 1px solid #1b4238;
  border-radius: 8px;
  color: #1b4238;
  background: #fff;
}

.sa-date-filter__input::-webkit-calendar-picker-indicator {
  cursor: pointer;
  opacity: 0.85;
}

.sa-date-filter__apply {
  margin: 4px 0 4px 4px;
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  background: #78b843;
  border: 1px solid #78b843;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
}

.sa-date-filter__apply:hover:not(:disabled) {
  background: #5e9a34;
  border-color: #5e9a34;
}

.sa-date-filter__apply:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.sa-date-filter__secondary {
  flex: 1 1 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px 8px;
  margin-top: 4px;
}

.sa-date-filter__link {
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  color: #3e3e3e;
  background: #fff;
  border: 1px solid #78b843;
  border-radius: 8px;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s ease, color 0.15s ease;
}

.sa-date-filter__link:hover:not(:disabled) {
  background: #78b843;
  color: #fff;
}

.sa-date-filter__link:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.sa-date-filter__sep {
  color: #8a8a8a;
  font-size: 13px;
}

.sa-date-filter__hint {
  font-size: 12px;
  color: #5c6d57;
}

@media (max-width: 720px) {
  .sa-date-filter {
    flex-direction: column;
    align-items: stretch;
  }

  .sa-date-filter__group {
    margin: 6px 0;
    justify-content: flex-start;
  }

  .sa-date-filter__input {
    flex: 1;
    min-width: 0;
    max-width: none;
  }

  .sa-date-filter__apply {
    margin: 6px 0 0;
    width: 100%;
  }

}

.sa-section__title--after-grid {
  margin-top: 22px;
}

.sa-subtabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 0 16px;
}

.sa-subtab {
  border: 1px solid #cfd8d3;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 500;
  color: #1b4238;
  background: #fff;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.sa-subtab:hover:not(.sa-subtab--active) {
  background: #f4faf0;
  border-color: #78b843;
}

.sa-subtab--active {
  background: #78b843;
  color: #fff;
  border-color: #78b843;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(120, 184, 67, 0.35);
}

.sa-subtab-panel {
  margin-bottom: 8px;
}

.sa-map-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 14px;
  margin: 0 0 12px;
}

.sa-map-toggle {
  appearance: none;
  border: 1px solid #78b843;
  background: #fff;
  color: #1b4238;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.sa-map-toggle:hover {
  background: #f4faf0;
}

.sa-map-toggle--active {
  background: #78b843;
  color: #fff;
  box-shadow: 0 2px 8px rgba(120, 184, 67, 0.35);
}

.sa-map-toolbar__hint {
  margin: 0;
  font-size: 12px;
  color: #8a3b12;
  flex: 1 1 220px;
  line-height: 1.35;
}

.sa-map-panel {
  margin-bottom: 16px;
}

.sa-snapshot__context {
  margin: -4px 0 12px;
  font-size: 14px;
  font-weight: 500;
  color: #4a5c46;
}

.sa-muted--below-cards {
  margin-top: 6px;
}

.sa-muted {
  margin: 0 0 16px;
  font-size: 14px;
  color: #666;
}

.sa-muted--tab-hint {
  margin: -4px 0 14px;
  font-size: 13px;
}

.sa-snapshot {
  margin-bottom: 14px;
}

.sa-snapshot__group-title {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 300;
  letter-spacing: normal;
  text-transform: uppercase;
  color: #3e3e3e;
}

.sa-snapshot__group-title:not(:first-child) {
  margin-top: 12px;
}

.sa-snapshot-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.sa-mini {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 12px 10px;
  min-height: 0;
  text-align: center;
  min-width: 0;
  transition: background 0.15s ease, box-shadow 0.15s ease;
}

.sa-mini:hover {
  background: #fbfbfb;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.sa-tone--neutral {
  background: linear-gradient(180deg, #f0f2f4 0%, #e4e7ea 100%);
  border: 1px solid #c5cad0;
}

.sa-tone--temp-cold {
  background: linear-gradient(180deg, #e3f0fb 0%, #c5daf5 100%);
  border: 1px solid #6b9fd4;
}

.sa-tone--temp-cool {
  background: linear-gradient(180deg, #e5f6f4 0%, #c8ebe6 100%);
  border: 1px solid #52b69a;
}

.sa-tone--temp-mild {
  background: linear-gradient(180deg, #eef6e6 0%, #d8ebc4 100%);
  border: 1px solid #78b843;
}

.sa-tone--temp-warm {
  background: linear-gradient(180deg, #fdf4e8 0%, #f8e0c0 100%);
  border: 1px solid #e9a23b;
}

.sa-tone--temp-hot {
  background: linear-gradient(180deg, #fdecea 0%, #f5c9c4 100%);
  border: 1px solid #d64545;
}

.sa-tone--hum-dry {
  background: linear-gradient(180deg, #eef5fc 0%, #dceaf8 100%);
  border: 1px solid #7a9ec9;
}

.sa-tone--hum-comfort {
  background: linear-gradient(180deg, #eef6ea 0%, #d9ebcf 100%);
  border: 1px solid #78b843;
}

.sa-tone--hum-moist {
  background: linear-gradient(180deg, #f4faf0 0%, #e2efd4 100%);
  border: 1px solid #95c96a;
}

.sa-tone--hum-wet {
  background: linear-gradient(180deg, #eaf4fb 0%, #d4e8f5 100%);
  border: 1px solid #168aad;
}

/* Amplitude térmica: só no card (tons discretos, barra lateral). */
.sa-amp-card.sa-tone--amp-low {
  background: #f8fbf8;
  border: 1px solid #e0ebe3;
  border-left: 4px solid #5a9c3a;
}

.sa-amp-card.sa-tone--amp-mid {
  background: #fcfaf6;
  border: 1px solid #ebe6dc;
  border-left: 4px solid #c49a1f;
}

.sa-amp-card.sa-tone--amp-high {
  background: #fdf9f9;
  border: 1px solid #ebe0e0;
  border-left: 4px solid #b83d3d;
}

/* Última leitura: mesmo padrão dos cards do lote (fundo branco; cor só no valor). */
.sa-mini.sa-mini--stat-min .sa-mini__value,
.sa-mini.sa-mini--stat-min .sa-mini__unit {
  color: #457b9d;
}

.sa-mini.sa-mini--stat-max .sa-mini__value,
.sa-mini.sa-mini--stat-max .sa-mini__unit {
  color: #a94949;
}

.sa-mini.sa-mini--stat-avg .sa-mini__value,
.sa-mini.sa-mini--stat-avg .sa-mini__unit {
  color: #78b843;
}

.sa-mini.sa-mini--stat-atual .sa-mini__value,
.sa-mini.sa-mini--stat-atual .sa-mini__unit {
  color: #e2bf65;
}

.sa-mini__label {
  display: block;
  font-size: 16px;
  font-weight: 300;
  text-transform: none;
  letter-spacing: normal;
  margin-bottom: 4px;
  color: #6b6c6c;
}

.sa-mini__value {
  display: block;
  flex: 0 0 auto;
  font-size: 20px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
  color: #78b843;
}

.sa-mini__unit {
  font-size: 14px;
  font-weight: 700;
  margin-left: 1px;
}

.sa-mini__time {
  display: block;
  width: 100%;
  margin-top: auto;
  padding-top: 6px;
  font-size: 12px;
  font-weight: 300;
  line-height: 1.35;
  color: #6b6c6c;
  font-variant-numeric: tabular-nums;
}

@media screen and (max-width: 640px) {
  .sa-snapshot-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }
}

.sa-loading-inline {
  padding: 16px;
  color: #666;
}

.warning {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
}

.warning--compact {
  min-height: 28vh;
  margin: 12px 0 20px;
}

.warning .text {
  background: #78b843;
  border-radius: 10px;
  color: #fff;
  padding: 20px;
  font-size: 30px;
  max-width: 92%;
  box-sizing: border-box;
}

.warning .text.text--error {
  background: #c94a4a;
}

.warning .text h1 {
  display: flex;
  justify-content: center;
  text-align: center;
  margin: 0 0 12px;
  font-size: 1em;
  font-weight: 600;
}

.warning .text p {
  display: flex;
  justify-content: center;
  margin: 0;
  font-size: 0.55em;
  text-align: center;
  line-height: 1.35;
}

.sa-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 18px;
}

.sa-grid.sa-grid--selectable {
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
  gap: 14px;
  width: 100%;
}

.sa-grid--selectable .sa-card--filled {
  box-shadow: 0 2px 10px rgba(27, 66, 56, 0.08), 0 1px 0 rgba(255, 255, 255, 0.75) inset;
}

.sa-grid--selectable .sa-card--filled:hover {
  box-shadow: 0 4px 14px rgba(27, 66, 56, 0.11), 0 1px 0 rgba(255, 255, 255, 0.82) inset;
}

.sa-grid--selectable .sa-card--selectable {
  cursor: pointer;
  transition:
    opacity 0.22s ease,
    filter 0.22s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease;
}

.sa-grid--selectable .sa-card--dimmed {
  opacity: 0.5;
  filter: saturate(0.7);
}

.sa-grid--selectable .sa-card--dimmed:hover {
  opacity: 0.66;
  transform: none;
}

.sa-grid--selectable .sa-card--selectable.sa-card--filled.sa-card--selected {
  opacity: 1;
  filter: none;
  transform: translateY(-1px);
  z-index: 2;
  border-color: #5a9a32;
  box-shadow:
    0 0 0 2px rgba(120, 184, 67, 0.4),
    0 8px 20px rgba(27, 66, 56, 0.16),
    0 1px 0 rgba(255, 255, 255, 0.9) inset;
}

.sa-grid--selectable .sa-card--selectable.sa-card--filled.sa-card--selected.sa-tone--temp-cold {
  border-color: #2d7dd2;
  box-shadow:
    0 0 0 2px rgba(90, 159, 212, 0.4),
    0 8px 20px rgba(45, 125, 210, 0.16),
    0 1px 0 rgba(255, 255, 255, 0.9) inset;
}

.sa-grid--selectable .sa-card--selectable.sa-card--filled.sa-card--selected.sa-tone--temp-cool {
  border-color: #2d6a4f;
  box-shadow:
    0 0 0 2px rgba(82, 182, 154, 0.4),
    0 8px 20px rgba(45, 106, 79, 0.14),
    0 1px 0 rgba(255, 255, 255, 0.9) inset;
}

.sa-grid--selectable .sa-card--selectable.sa-card--filled.sa-card--selected.sa-tone--temp-warm {
  border-color: #d68a1f;
  box-shadow:
    0 0 0 2px rgba(233, 162, 59, 0.45),
    0 8px 20px rgba(214, 138, 31, 0.16),
    0 1px 0 rgba(255, 255, 255, 0.9) inset;
}

.sa-grid--selectable .sa-card--selectable.sa-card--filled.sa-card--selected.sa-tone--temp-hot {
  border-color: #b83232;
  box-shadow:
    0 0 0 2px rgba(214, 69, 69, 0.4),
    0 8px 20px rgba(184, 50, 50, 0.15),
    0 1px 0 rgba(255, 255, 255, 0.9) inset;
}

.sa-grid--selectable .sa-card--selectable.sa-card--filled.sa-card--selected.sa-tone--temp-mild {
  border-color: #5a9a32;
  box-shadow:
    0 0 0 2px rgba(120, 184, 67, 0.45),
    0 8px 20px rgba(90, 154, 50, 0.16),
    0 1px 0 rgba(255, 255, 255, 0.9) inset;
}

.sa-grid--selectable .sa-card--empty {
  cursor: default;
}

.sa-card__datetime {
  margin: 0 16px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #5c6d57;
  letter-spacing: 0.02em;
}

.sa-card {
  border-radius: 16px;
  padding: 0;
  border: 1px solid #d8d8d8;
  overflow: hidden;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.sa-card:hover {
  transform: translateY(-2px);
}

.sa-card--filled {
  background: linear-gradient(155deg, #fbfcfa 0%, #f0f7ea 38%, #e4efd9 100%);
  border-width: 1px;
  box-shadow: 0 6px 22px rgba(27, 66, 56, 0.1), 0 1px 0 rgba(255, 255, 255, 0.8) inset;
}

.sa-card--filled:hover {
  box-shadow: 0 10px 28px rgba(27, 66, 56, 0.14), 0 1px 0 rgba(255, 255, 255, 0.85) inset;
}

.sa-card--filled.sa-tone--temp-cold {
  border-color: #6b9fd4;
  box-shadow: 0 6px 22px rgba(45, 125, 210, 0.12), inset 0 -3px 0 #6b9fd4;
}

.sa-card--filled.sa-tone--temp-cool {
  border-color: #52b69a;
  box-shadow: 0 6px 22px rgba(82, 182, 154, 0.12), inset 0 -3px 0 #52b69a;
}

.sa-card--filled.sa-tone--temp-mild {
  border-color: #78b843;
  box-shadow: 0 6px 22px rgba(120, 184, 67, 0.14), inset 0 -3px 0 #78b843;
}

.sa-card--filled.sa-tone--temp-warm {
  border-color: #e9a23b;
  box-shadow: 0 6px 22px rgba(233, 162, 59, 0.14), inset 0 -3px 0 #e9a23b;
}

.sa-card--filled.sa-tone--temp-hot {
  border-color: #d64545;
  box-shadow: 0 6px 22px rgba(214, 69, 69, 0.14), inset 0 -3px 0 #d64545;
}

.sa-card--empty {
  background: linear-gradient(180deg, #fafafa 0%, #ececec 100%);
  padding: 18px 16px;
}

.sa-card--empty:hover {
  transform: none;
  box-shadow: none;
}

.sa-card__head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 16px 12px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.65) 0%, rgba(255, 255, 255, 0) 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.sa-card__icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  font-size: 14px;
  color: #fff;
  background: linear-gradient(145deg, #78b843 0%, #5a9a32 100%);
  box-shadow: 0 2px 8px rgba(120, 184, 67, 0.35);
}

.sa-card--filled.sa-tone--temp-cold .sa-card__icon {
  background: linear-gradient(145deg, #5a9fd4 0%, #2d7dd2 100%);
  box-shadow: 0 2px 8px rgba(45, 125, 210, 0.35);
}

.sa-card--filled.sa-tone--temp-cool .sa-card__icon {
  background: linear-gradient(145deg, #52b69a 0%, #2d6a4f 100%);
  box-shadow: 0 2px 8px rgba(82, 182, 154, 0.35);
}

.sa-card--filled.sa-tone--temp-warm .sa-card__icon {
  background: linear-gradient(145deg, #f4a261 0%, #d6a424 100%);
  box-shadow: 0 2px 8px rgba(233, 162, 59, 0.35);
}

.sa-card--filled.sa-tone--temp-hot .sa-card__icon {
  background: linear-gradient(145deg, #e76f51 0%, #c62d2d 100%);
  box-shadow: 0 2px 8px rgba(214, 69, 69, 0.35);
}

.sa-card--filled.sa-tone--temp-mild .sa-card__icon {
  background: linear-gradient(145deg, #99d98c 0%, #78b843 100%);
  box-shadow: 0 2px 8px rgba(120, 184, 67, 0.35);
}

.sa-card__name {
  margin: 0;
  flex: 1;
  min-width: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1b4238;
  letter-spacing: 0.01em;
  line-height: 1.25;
  border: none;
  padding: 0;
}

.sa-card__metrics {
  padding: 14px 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.sa-card__metric-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #5c6d57;
  margin-bottom: 4px;
}

.sa-card__nodata {
  margin: 0;
  padding: 8px 16px 20px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #8a8a8a;
}

.sa-card__line {
  margin: 0;
  text-align: left;
  font-size: 22px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #1b4238;
  line-height: 1.2;
}

.sa-card__line:last-child {
  margin-bottom: 0;
}

.sa-card__line--humidity {
  color: #2d6a4f;
  font-size: 20px;
}

.sa-grid--selectable .sa-card {
  border-radius: 12px;
}

.sa-grid--selectable .sa-card__head--compact {
  padding: 8px 10px 6px;
  gap: 8px;
  border-bottom: none;
  background: none;
}

.sa-grid--selectable .sa-card__head-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 2px 8px;
}

.sa-grid--selectable .sa-card__datetime--inline {
  margin: 0;
  font-size: 10px;
  font-weight: 500;
  color: #5c6d57;
  white-space: nowrap;
}

.sa-grid--selectable .sa-card__grid-compact {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 10px;
  padding: 4px 10px 6px;
}

.sa-grid--selectable .sa-card__kv {
  display: flex;
  align-items: baseline;
  gap: 5px;
  min-width: 0;
}

.sa-grid--selectable .sa-card__kv-k {
  flex-shrink: 0;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #5c6d57;
}

.sa-grid--selectable .sa-card__kv-v {
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #1b4238;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sa-grid--selectable .sa-card__kv-v--humidity {
  color: #2d6a4f;
}

.sa-grid--selectable .sa-card__kv-v--coord {
  font-size: 11px;
  font-weight: 600;
}

.sa-grid--selectable .sa-card__maps-link--compact {
  display: block;
  padding: 0 10px 8px;
  font-size: 10px;
  font-weight: 600;
  color: #2d6a4f;
  text-decoration: none;
  text-align: right;
}

.sa-grid--selectable .sa-card__maps-link--compact:hover {
  text-decoration: underline;
}

.sa-grid--selectable .sa-card__nodata--compact {
  padding: 6px 10px 10px;
  font-size: 12px;
}

.sa-grid--selectable .sa-card__head {
  padding: 10px 12px 8px;
  gap: 8px;
}

.sa-grid--selectable .sa-card__icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  font-size: 12px;
}

.sa-grid--selectable .sa-card__name {
  font-size: 14px;
  line-height: 1.2;
}

.sa-card__coords {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px 12px;
  width: 100%;
}

.sa-card__line--coord {
  font-size: 13px;
  font-weight: 600;
}

.sa-card__maps-link {
  flex: 1 1 100%;
  font-size: 12px;
  font-weight: 600;
  color: #2d6a4f;
  text-decoration: none;
}

.sa-card__maps-link:hover {
  text-decoration: underline;
}

.sa-amp-wrap {
  width: 100%;
  max-width: 560px;
  margin-bottom: 12px;
}

.sa-amp-card {
  width: 100%;
  max-width: none;
  border-radius: 10px;
  padding: 10px 12px 10px 11px;
  box-shadow: 0 1px 4px rgba(27, 66, 56, 0.06);
}

.sa-amp-card__top {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px 20px;
}

.sa-amp-card__lead {
  flex: 1 1 160px;
  min-width: 0;
}

.sa-amp-card__figure {
  flex: 0 0 auto;
  text-align: right;
  min-width: 0;
}

@media screen and (max-width: 420px) {
  .sa-amp-card__figure {
    flex: 1 1 100%;
    text-align: left;
  }

  .sa-amp-card__meta {
    margin-left: 0;
    max-width: none;
  }
}

.sa-amp-card__eyebrow {
  margin: 0 0 1px;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: rgba(30, 50, 40, 0.5);
}

.sa-amp-card__context {
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 600;
  color: #2d6a4f;
}

.sa-amp-card__title {
  margin: 0 0 2px;
  font-size: 13px;
  font-weight: 600;
  color: #1b4238;
  line-height: 1.25;
}

.sa-amp-card__day {
  margin: 0;
  font-size: 11px;
  font-weight: 500;
  color: #5a6b62;
}

.sa-amp-card__value {
  margin: 0 0 2px;
  font-size: 22px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #14221c;
  line-height: 1.05;
}

.sa-amp-card__unit {
  font-size: 13px;
  font-weight: 600;
  margin-left: 2px;
  opacity: 0.88;
}

.sa-amp-card__meta {
  margin: 0;
  font-size: 11px;
  color: #5a6b62;
  line-height: 1.35;
  max-width: 220px;
  margin-left: auto;
}

.sa-amp-card__more-title {
  margin: 8px 0 4px;
  padding-top: 6px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(30, 50, 40, 0.5);
}

.sa-amp-card__list {
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 11px;
  color: #5a6b62;
}

.sa-amp-card__list li {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 10px;
  padding: 3px 0;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.sa-amp-card__list-amp {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #1b4238;
}

.sa-chart-box {
  height: 400px;
  position: relative;
  margin-bottom: 24px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
}

.sa-chart-box__inner {
  height: 100%;
}

.sa-table-wrap {
  overflow-x: auto;
  border: 1px solid #dfe8e3;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 1px 4px rgba(27, 66, 56, 0.06);
  padding: 12px;
}

.sa-table-toggle {
  appearance: none;
  border: 1px solid #78b843;
  background: #fff;
  color: #1b4238;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.sa-table-toggle:hover {
  background: #f4faf0;
}

.sa-table {
  width: 100%;
  min-width: 560px;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  line-height: 1.35;
  color: #243530;
}

.sa-table__th {
  padding: 11px 14px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(27, 66, 56, 0.75);
  background: linear-gradient(180deg, #f2f7f4 0%, #e8f0eb 100%);
  border-bottom: 1px solid #cddfd4;
  white-space: nowrap;
}

.sa-table__th--num {
  text-align: right;
}

.sa-table__td {
  padding: 10px 14px;
  vertical-align: middle;
  border-bottom: 1px solid #ecf2ee;
  transition: background-color 0.12s ease;
}

.sa-table__td--datetime {
  font-size: 12px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: #4a5c54;
  white-space: nowrap;
}

.sa-table__td--sensor {
  min-width: 140px;
  max-width: 280px;
}

.sa-table__sensor-name {
  display: inline-block;
  max-width: 100%;
  padding: 3px 9px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #1b4238;
  background: #eef6f0;
  border: 1px solid #c8dfd0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.sa-table__td--num {
  text-align: right;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #1b3028;
  white-space: nowrap;
}

.sa-table tbody tr:nth-child(even) .sa-table__td {
  background: #fafcfb;
}

.sa-table tbody tr:hover .sa-table__td {
  background: #f0f7f2;
}

.sa-table tbody tr:last-child .sa-table__td {
  border-bottom: none;
}
</style>
